import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
// Employee Scorecard — project-agnostic, table-driven (Phase 2 + Phase 3 shell).
//
// Phase 1+2 replaced the per-project hardcoding of the Apple Behavioural
// Scorecard / Quadrant / Targets-as-score views with one configurable
// scorecard per project. Phase 3 (this iteration) makes this page the single
// entry point for every related admin workflow — Targets & Achievements
// upload, Trainings CRUD, Training Attendance, and Seed Score review — via
// a View / Manage tab layout. The legacy AppleScorecardPage and TargetsPage
// components are reused inline (with their `embedded` prop set) rather than
// duplicated; the legacy /reports/apple-scorecard, /reports/employee-quadrants,
// and /master-management/targets routes redirect here so old bookmarks land
// on the right Manage sub-tab via the `?tab=` query param.
//
// Tab structure:
//   View  → Score, Quadrant
//   Manage → Targets & Achievements, Trainings, Attendance, Seed Scores, Configure

import PerEmployeeMetricScoreTable, {
  compareMetricScoreRows, initialMetricScoreSortDir, metricScoreSheet,
  type MetricScoreSortKey, type PerEmployeeMetricScoreRow,
} from '../components/PerEmployeeMetricScoreTable';
import InactiveScoresPanel, {
  StaffViewTabs, buildInactiveScoreSheet, visibleInactiveRows,
  type InactiveScoreRow, type StaffView,
} from './employeeScore/InactiveScoresPanel';
import { SearchInput } from '../components/SearchInput';
import { InlineLoader } from '../components/loading';
import { downloadTableXlsx, fileSlug, type TableSheet, type TableSheetGetter } from '../utils/tableXlsx';
import { buildMonthlyScoreSheet, buildSixMonthScoreSheet } from './employeeScore/scoreViewSheets';
import AwaitingUploadsNotice, { type AwaitedUpload } from './employeeScore/AwaitingUploadsNotice';
import SalesFeedWaitingBar, { type SalesFeedWaiting } from './employeeScore/SalesFeedWaitingBar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, MapPin, User, Users, Store, ChevronDown, X, Star, TrendingUp, Target, AlertTriangle, ShieldAlert, HelpCircle, Sparkles, PauseCircle, type LucideIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { useLinkedSearch, type SearchLink } from '../hooks/useLinkedSearch';
import { scorecardAPI, rolesAPI, targetMetricsAPI } from '../services/api';
import AppleScorecardPage from './AppleScorecardPage';
import TargetsPage from './TargetsPage';
import ManageMetricsModal from '../components/ManageMetricsModal';
import SubMetricsManagePanel, { type MetricScoreRow } from '../components/SubMetricsManagePanel';

type Quadrant = 'Stars' | 'Achievers' | 'Strivers' | 'At Risk' | 'Unclassified' | 'New Joiner' | 'Excluded';

interface SlabRow {
  lower_bound: number | null;
  upper_bound: number | null;
  points: number;
  display_label?: string | null;
  display_order?: number;
}

interface CatalogEntry {
  metric_def_id: number;
  project_id: number | null;
  code: string;
  kind: 'system' | 'task_question' | 'target_metric';
  name: string;
  unit: string | null;
  higher_is_better: boolean;
  task_question_id: string | null;
  target_metric_id: number | null;
  // Stored formula for the parent target_metric (null for other kinds, or
  // for custom metrics that haven't been configured yet). Surfaced in the
  // "How is this calculated?" panel so the admin sees the exact expression
  // driving the score.
  target_metric_formula: string | null;
  // True when the calculation of this custom metric has an input read from
  // Sales & Targets. Those figures are stored per person (a manager's is the
  // team's total), so the Configure row locks the scope to Employee. Absent on
  // an API older than that source.
  has_sales_feed?: boolean;
}

interface ConfigEntry {
  config_id: number;
  metric_def_id: number;
  code: string;
  kind: CatalogEntry['kind'];
  name: string;
  weight_pct: number;
  display_order: number;
  // Per-config additions from migration 2026-05-28-scorecard-role-scope.
  // role_id null = project-default config; attribution_scope governs the
  // value-loader (employee-level vs store-fan-out).
  role_id: string | null;
  attribution_scope: 'employee' | 'store';
  // Per-metric tuning knobs (migration 2026-05-28-scorecard-config-params).
  // Currently used only by seed_score; null = engine defaults.
  config_params: Record<string, any> | null;
  // Score basis (migration 2026-05-28-target-metric-task-source). Only
  // meaningful for kind = 'target_metric'. 'raw' (default) scores the
  // achievement value directly; 'achievement_pct' scores achievement / target
  // × 100. Other kinds ignore this at compute time.
  score_basis: 'raw' | 'achievement_pct';
  // Pause/resume signal. The Configure-tab checkbox toggles this between
  // 'active' (counts toward scoring) and 'inactive' (preserved but
  // skipped by the engine and the visible weight sum). Lets admins
  // temporarily pause a metric without losing its weight + slabs.
  status: 'active' | 'inactive';
  slabs: SlabRow[];
}

// Static formula descriptions per metric code — shown as a "How is this
// calculated?" card on Configure. Keeps the engine logic transparent so the
// admin understands what slabs + weights are actually being applied to.
const METRIC_FORMULAS: Record<string, { summary: string; details: string }> = {
  am_feedback: {
    summary: 'Average rating on the flagged Rating task question, normalized to 0–100% (rating ÷ question’s max rating × 100).',
    details: 'For each employee: avg(answer_value) over every submission of the question marked "Use in Apple Scorecard (AM Feedback)" for the month, then scaled to a 0–100% using the question’s configured max rating (max/max = 100%). Employees with zero samples contribute 0 to their row. Slabs map the percentage to points.',
  },
  seed_score: {
    summary: 'Cohort-relative score against same-project peers in the same Age-on-Network (AON) band. Honeymoon rule shields new joiners.',
    details: 'Pipeline: (1) DOJ < honeymoon window months → flat honeymoon points (no peer comparison). (2) Otherwise bucket the employee into an AON band: 3–6m, 6–9m, 9–12m, 1–2y, 2y+. (3) Within the cohort, compute median + σ. (4) Score > median+σ = top tier; ≥ median = high; ≥ median−σ = low; below = bottom. Slabs are NOT used — points come directly from this engine.',
  },
  grooming_avg: {
    summary: 'Average composite grooming score (/10) across all in-store visits, normalized to 0–100% (score ÷ 10 × 100).',
    details: 'Each grooming check writes a composite from a multi-axis evaluation. We average them across the month, then scale to 0–100% (10/10 = 100%); slabs map the percentage to points.',
  },
  random_attendance_pct: {
    summary: 'Percentage of random-attendance slots the employee responded to (already 0–100%).',
    details: 'marked_count / received_count × 100. Slabs reward higher response %.',
  },
  leave_policy: {
    summary: 'Attendance % = (1 − absent days ÷ days in the month) × 100. Higher is better.',
    details: 'Absent days = cp_attendance absences minus approved leaves / week-offs / holidays. That count is converted to an attendance percentage over the calendar days in the month (e.g. 1 absent day in a 31-day month → ≈96.8%); slabs map the percentage to points.',
  },
  on_time_pct: {
    summary: 'On-time % = (1 − late check-ins ÷ days in the month) × 100. Higher is better.',
    details: 'Late check-ins are counted against the HRMS shift start time (or stores.shift_start_time fallback), then converted to an on-time percentage over the calendar days in the month; slabs map the percentage to points.',
  },
  training_pct: {
    summary: 'Trainings attended divided by trainings scheduled, as a percentage (already 0–100%).',
    details: 'attended_count / scheduled_count × 100. Special case: an employee with no scheduled trainings receives full credit (assumed compliant). Source data is recorded under Grow → Offline Trainings.',
  },
  lms_points: {
    summary: 'Learning points earned in the month ÷ the monthly target × 100. Earning the target or more is 100%.',
    details: 'Points are the ones the mobile Grow tab awards — completing modules, reaching journey milestones and attending LMS training sessions — the same points the Grow → Leaderboard "This Month" list ranks. Only points earned in the scored month count. Example: with a monthly target of 200, an employee who earned 150 points is at 75%; 200 or more is 100%. Slabs map the percentage to points. An employee who earned nothing in the month has no value, so the "No value" setting decides their score.',
  },
  task_question: {
    summary: 'Numeric answer to the flagged task question, averaged over the month if multiple submissions.',
    details: 'For each employee: avg(answer_text::numeric) across every submission of the flagged question in the month. Slabs map the average to points.',
  },
  target_metric: {
    summary: 'Raw achieved_value from the targets table for this metric, attributed by either store or employee.',
    details: 'When the inclusion is set to Store-scope, the store\'s achievement is attributed to every deployed employee. When set to Employee-scope, each employee\'s individual achievement is used. Slabs map raw values to points; bonus tiers above 100 are allowed. Each input comes from a manual upload, a task question or Sales & Targets.',
  },
  team_avg_score: {
    summary: 'Mean scorecard total of the manager\'s DIRECT reports for the month. Scored after every individual total is final.',
    details: 'Direct reports only — a Trade Head averages their Area Managers (each already carrying their own team average), not the whole subtree beneath them. A reportee counts only if they have a score for the month: unscored, pre-joining and maternity-excluded people are dropped from both the sum and the divisor, so they neither help nor hurt. New joiners DO count from month one — this metric measures the manager, and excluding them would let the number ignore the part of the team most needing attention. With no eligible reportee at all, the metric falls back to its configured no-value rule. A manager sees the reportee names and scores behind their own average on the mobile scorecard.',
  },
};

interface RoleOption {
  id: string;
  name: string;
  user_count: number;
}

// System metrics whose data feed isn't set up for this project yet. The
// backend hides them from `catalog` and surfaces them here so the UI can
// guide the admin to enable the underlying capture (task-question flag,
// mobile feature grant, etc.) before they can be scored.
interface UnavailableMetric {
  code: string;
  name: string;
  kind: 'system';
  reason: string;
}

// Metric codes whose loader produces a value that's only meaningful at the
// individual level — store-fan-out doesn't make conceptual sense for them.
// The Configure UI disables the Employee/Store toggle with a tooltip for
// these rows. Everything else (grooming, random attendance, leave, on-time,
// training participation, task_question, target_metric) can be toggled
// between employee-level and store-level (average of the store's employees
// applied back to every team member).
const EMPLOYEE_ONLY_METRIC_CODES = new Set<string>([
  // AM Feedback rates a specific individual; averaging across the store
  // would mean "everyone in the store gets the same rating", which contradicts
  // the rater's intent.
  'am_feedback',
  // Seed Score points come from cohort-relative math (AON buckets + median
  // ± σ). Averaging cohort-relative points by store would mix cohorts in
  // a way the engine wasn't designed for.
  'seed_score',
]);

// Seed Score is cohort-scored inside the loader — its slabs are read but
// ignored at compute time. The Configure tab disables the slabs editor for
// this code and surfaces a notice.
const COHORT_SCORED_CODES = new Set<string>(['seed_score']);

// Legacy SYSTEM metrics retired from the "add metric" picker — AM Feedback and
// Seed Score are now configured as custom task-question-sourced metrics. They're
// hidden from the catalog list unless a config already includes them (so an
// existing setup stays visible/editable). The loaders + the old
// /reports/apple-scorecard report are left intact.
const RETIRED_SYSTEM_CODES = new Set<string>(['am_feedback', 'seed_score']);

// Not a metric: the engine adds this to meta.failed_metrics when the 1Office name
// lookup failed, so some names fell back to employee codes. Every score is
// intact, so it gets a quiet line rather than the "metric couldn't be computed"
// warning. The server keeps such a result for only a minute, so a reload heals it.
const NAME_LOOKUP_FAILED_CODE = 'employee_names';

// System metrics whose raw value the engine normalizes to a 0–100% scale before
// the slab lookup. For these, slabs are defined on 0–100, the displayed unit is
// "%", and direction is always "higher is better" (count metrics are inverted to
// attendance%/on-time%). The hint explains what raw value maps to 100%.
const NORMALIZED_METRIC_HINT: Record<string, string> = {
  grooming_avg: '100% = grooming composite 10/10',
  am_feedback: '100% = the rating question’s max rating (max/max)',
  random_attendance_pct: 'already a 0–100% response rate',
  training_pct: 'already a 0–100% attendance rate',
  leave_policy: '100% = zero absent days; (1 − absent ÷ days-in-month) × 100',
  on_time_pct: '100% = never late; (1 − late ÷ days-in-month) × 100',
  lms_points: '100% = the monthly target of learning points; (points earned ÷ target) × 100',
};

// Which numeric column the per-metric "Per-employee scores" table is sorted
// by. `value` can be null (no metric entry); those rows sink to the bottom
// regardless of direction. Mirrors the SortableTh idiom on AppleScorecardPage.
type ScoreSortKey = MetricScoreSortKey;

// View → Score text columns. Keys are wrapped in double underscores (like
// '__total__') so they can never collide with a metric `code`; being text, they
// sort A→Z first (see handleViewSort).
const SCORE_TEXT_SORT_KEYS = new Set<string>([
  '__emp_name__', '__emp_code__', '__store_name__', '__store_code__', '__store_address__', '__quadrant__',
]);

/** Generic clickable sort header used by the View → Score table, where the
 *  sort key is a string (a metric `code`, or the literal '__total__').
 *  Shows a faint up/down arrow until it is the sorted column — without it nothing
 *  on the header says it can be clicked — then the direction in the accent colour.
 *  `sticky` pins it to the top of a scrolling table region (header freeze);
 *  `pinClassName` replaces those pinning classes — the Employee Name header is
 *  also pinned to the left, above the other headers. */
function SortHeader({
  label, active, dir, align = 'right', sticky, pinClassName, onClick,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
  sticky?: boolean;
  pinClassName?: string;
  onClick: () => void;
}) {
  return (
    <th
      className={`whitespace-nowrap px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none bg-gray-50 hover:bg-gray-100 ${pinClassName ?? (sticky ? 'sticky top-0 z-20' : '')}`}
      onClick={onClick}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        {label}
        {active ? (
          dir === 'asc'
            ? <ArrowUp className="h-3 w-3 text-primary-600" aria-hidden="true" />
            : <ArrowDown className="h-3 w-3 text-primary-600" aria-hidden="true" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-gray-400" aria-hidden="true" />
        )}
      </span>
    </th>
  );
}

/** Modern filter "pill": a styled native <select> with a leading icon, a custom
 *  chevron, and a highlighted active state when a value is selected. Used by the
 *  View → Score AM / Trade / City filters. */
function FilterSelect({
  icon: Icon, value, onChange, allLabel, options,
}: {
  icon: LucideIcon;
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  // { value, label }: value is what the filter matches on (e.g. an AM manager CODE),
  // label is what's shown. For plain string dimensions value === label.
  options: Array<{ value: string; label: string }>;
}) {
  const active = value !== '';
  return (
    <div className="relative">
      <Icon
        className={`pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 ${active ? 'text-primary-600' : 'text-gray-400'}`}
      />
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`appearance-none cursor-pointer rounded-lg border py-1.5 pl-8 pr-8 text-sm font-medium shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary-200 ${
          active
            ? 'border-primary-300 bg-primary-50 text-primary-800'
            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
        }`}
      >
        <option value="">{allLabel}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown
        className={`pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 ${active ? 'text-primary-500' : 'text-gray-400'}`}
      />
    </div>
  );
}

interface ScoreRow {
  employee_code: string;
  employee_name: string;
  store_name: string;
  store_code: string;
  // From stores.address — surfaced as its own column on the Score tab.
  store_address: string;
  // Org dimensions for the View → Score filters (AM / Store Trade / City). NOTE: the
  // AM filter matches on `manager_code` (below), NOT `area_manager` (the name) — the
  // name is a separate CRS lookup that can be empty/inconsistent while the code
  // resolves, which silently dropped employees from the AM filter even though the
  // Dashboard (code-based) still showed them.
  store_trade?: string;
  city?: string;
  area_manager?: string;
  // Role id the engine resolved for this employee. Drives the View-tab
  // role-pill filter so selecting "Trade Heads" hides employees from
  // other roles. Null when no role mapping was found.
  role_id: string | null;
  metrics: Array<{
    code: string;
    name: string;
    value: number | null;
    points: number;
    contribution: number;
    weight_pct: number;
  }>;
  total: number;
  quadrant: Quadrant;
  // Standing within the employee's direct-manager team — the SAME rank the mobile
  // leaderboard shows (by monthly total). manager_rank_total is the peer-group size.
  // Both null when the employee has no resolvable manager.
  manager_rank?: number | null;
  manager_rank_total?: number | null;
  // Direct manager's code — the grouping key for the per-manager rank / leaderboard.
  manager_code?: string | null;
  /** Trade Head = the AM's manager (two hops). Drives the Trade Head filter. */
  th_code?: string | null;
  th_name?: string | null;
  // store_employees.date_of_joining — drives the AON (Age on Network) column.
  date_of_joining?: string | null;
  // True when this month is EARLIER than the employee's joining month — they had not
  // joined yet, so the backend returns a blank row (empty `metrics`, `total: 0`). The
  // parameter columns already render "—" for a missing metric; the total cell also
  // shows "—" (see below) rather than a fabricated 0.00, and the row is a New Joiner
  // with no rank. Absent/false = a normally-scored month.
  preJoin?: boolean;
  // Bottom Box = bottom 3% of the employee's direct-manager subtree by 6-month
  // average — a strict subset of the (bottom-25%) At Risk band. Computed 6-mo-wide
  // by the backend and attached to BOTH the monthly and 6-month rows, so the
  // Bottom Box summary card + row marker show in either view.
  is_bottom_box?: boolean;
}

// A live monthly ScoreRow with the 6-month snapshot grafted on. `six` is null in
// monthly mode and carries the per-month totals + the in-tenure-mean average and
// the backend's role-grouped quadrant in the 6-month view. In the 6-month view
// `quadrant` is overridden to `six.quadrant` (see mergedRows) — the SAME value the
// Dashboard "Standing" reads, so the two never disagree. All ScoreRow fields
// (name/store/role) survive so filters, role pills and sticky columns keep working.
type MergedRow = ScoreRow & {
  six: { totals: Record<string, number>; avg: number | null; months_count: number; quadrant: Quadrant } | null;
};

// User-facing labels per metric kind. The DB enum value 'target_metric' is
// kept for backward compat (table name target_metrics) but in the UI we
// surface it as "Custom" — these are admin-defined metrics that may or
// may not be tied to a target/achievement workflow.
const KIND_LABEL: Record<CatalogEntry['kind'], string> = {
  system: 'System',
  task_question: 'Task Question',
  target_metric: 'Custom',
};

const QUADRANT_STYLE: Record<Quadrant, { bg: string; text: string; ring: string; icon: LucideIcon; accent: string; iconBg: string }> = {
  'Stars':        { bg: 'bg-emerald-50',  text: 'text-emerald-700',  ring: 'ring-emerald-300', icon: Star,          accent: 'text-emerald-600', iconBg: 'bg-emerald-100' },
  'Achievers':    { bg: 'bg-sky-50',      text: 'text-sky-700',      ring: 'ring-sky-300',     icon: TrendingUp,    accent: 'text-sky-600',     iconBg: 'bg-sky-100' },
  'Strivers':     { bg: 'bg-amber-50',    text: 'text-amber-800',    ring: 'ring-amber-300',   icon: Target,        accent: 'text-amber-600',   iconBg: 'bg-amber-100' },
  'At Risk':      { bg: 'bg-rose-50',     text: 'text-rose-700',     ring: 'ring-rose-300',    icon: AlertTriangle, accent: 'text-rose-600',    iconBg: 'bg-rose-100' },
  'Unclassified': { bg: 'bg-gray-100',    text: 'text-gray-600',     ring: 'ring-gray-300',    icon: HelpCircle,    accent: 'text-gray-500',    iconBg: 'bg-gray-200' },
  'New Joiner':   { bg: 'bg-indigo-50',   text: 'text-indigo-700',   ring: 'ring-indigo-300',  icon: Sparkles,      accent: 'text-indigo-600',  iconBg: 'bg-indigo-100' },
  'Excluded':     { bg: 'bg-slate-100',   text: 'text-slate-700',    ring: 'ring-slate-300',   icon: PauseCircle,   accent: 'text-slate-600',   iconBg: 'bg-slate-200' },
};

const QUADRANT_ORDER: Quadrant[] = ['Stars', 'Achievers', 'Strivers', 'At Risk'];
// Bottom Box is NOT a quadrant label — it's an overlapping subset flag (the bottom
// 5% by 6-month average within each manager's team, a subset of the bottom-25% At
// Risk band). It gets its own stat card + row marker, styled a deeper red than the
// rose "At Risk" so the critical tail reads as more severe.
const BOTTOM_BOX_KEY = 'Bottom Box' as const;
const BOTTOM_BOX_STYLE: { bg: string; text: string; ring: string; icon: LucideIcon; accent: string; iconBg: string } =
  { bg: 'bg-red-100', text: 'text-red-800', ring: 'ring-red-400', icon: ShieldAlert, accent: 'text-red-700', iconBg: 'bg-red-200' };
const BOTTOM_BOX_TITLE = 'The bottom 3% by 6-month average within each manager’s team — the most critical subset of At Risk. An employee here is also counted under At Risk.';
// Explanatory tooltips for the non-ranked tiles (shown as stat cards alongside the tiers).
const QUADRANT_TITLE: Partial<Record<NonNullable<Quadrant>, string>> = {
  'New Joiner': 'Recent joiners (under 6 months in the org) — held out of ranking until they have enough history to be fairly scored.',
  'Unclassified': 'Employees with no values on any included metric, or whose role has no override config.',
  'Excluded': 'Currently on maternity leave — held out of quadrant ranking while on leave, so the period of leave never counts against their standing.',
};

// AON (Age on Network) = CURRENT tenure, whole months from date_of_joining to TODAY —
// deliberately independent of the viewed month, so a recent joiner still shows a value
// even when an earlier month is selected (viewing June for someone who joined in July
// would otherwise read '—'). null when DOJ is unknown or in the future.
function aonMonthsToday(doj?: string | null): number | null {
  if (!doj) return null;
  const start = new Date(doj);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  return months < 0 ? null : months;
}
function aonLabelFrom(doj?: string | null): string {
  const months = aonMonthsToday(doj);
  if (months == null) return '—';
  if (months === 0) return '<1m';
  const yrs = Math.floor(months / 12);
  const mos = months % 12;
  if (yrs > 0 && mos > 0) return `${yrs}y ${mos}m`;
  if (yrs > 0) return `${yrs}y`;
  return `${mos}m`;
}
// DEPRECATED pass-through. The 'New Joiner' badge was retired (product change): recent
// joiners are ranked into their real quadrant like everyone else, so this no longer
// upgrades 'Unclassified' → 'New Joiner'. Kept as an identity fn so callers are untouched.
function withNewJoinerQuadrant(q: Quadrant, _doj?: string | null): Quadrant {
  return q;
}

/** What the Score table's free text matches: employee name / code and store name /
 *  code. `q` is already trimmed and lower-cased. */
const scoreRowMatchesText = (r: ScoreRow, q: string): boolean =>
  `${r.employee_name ?? ''} ${r.employee_code ?? ''} ${r.store_name ?? ''} ${r.store_code ?? ''}`
    .toLowerCase().includes(q);

const firstOfThisMonth = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Short month-column label for the 6-month view, e.g. '2026-01-01' → 'Jan 26'.
const fmtMonthShort = (v: string): string =>
  new Date(`${v.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });

// Effective-dated config version labels. The sentinel 1900-01-01 is the
// pre-existing config that has no explicit start month — it covers everything
// up to the first real version, so we render it as "Initial".
const SENTINEL_VERSION = '1900-01-01';
const fmtVersionMonth = (v: string): string =>
  v === SENTINEL_VERSION
    ? 'Initial'
    : new Date(`${v}T00:00:00Z`).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
// Label for the month immediately BEFORE a version's start — i.e. the last
// month the previous version still covers (e.g. version 2026-06-01 → "May 2026").
const monthBeforeLabel = (v: string): string => {
  const d = new Date(`${v}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
};

// Two-level tab key. Each value is exactly the URL `?tab=` value we accept and
// render — the legacy-redirect targets land on one of these. Adding a new tab
// means listing it here and rendering its block below.
//
// 'quadrant' is retained as a legacy alias that lands on the Score tab (the
// quadrant counts are now a chip strip above the score table — no separate
// tab any more). The legacy redirect from /reports/employee-quadrants keeps
// working transparently.
// 'metric:<metric_def_id>' is the per-included-metric sub-tab pattern — one
// per metric in any role's config, regardless of kind. Each tab shows the
// metric's data-entry surface (TargetsPage for target_metric, OCR review
// for seed_score) plus a per-employee scores table filtered to that metric.
//
// Legacy 'targets'/'seed'/'targets:<id>' values are kept for back-compat
// with old deep links; they normalise to 'metric:<id>' or 'configure' at
// render time.
type TabKey =
  | 'score' | 'quadrant'
  | 'targets' | `targets:${number}`
  | `metric:${number}`
  | 'trainings' | 'attendance' | 'seed' | 'configure';

const VIEW_TABS: { key: TabKey; label: string }[] = [
  { key: 'score', label: 'Score' },
];
// MANAGE sub-tabs are now derived per-project via `visibleManageTabs` inside
// the component — irrelevant tabs are hidden when their data feed isn't set
// up. Configure is always present as a fallback.

// 'quadrant' is treated as a Score-tab alias (legacy redirect target) so
// deep links from /reports/employee-quadrants land on Score with the quadrant
// strip rendered above the table.
const isViewTab = (k: TabKey) => k === 'quadrant' || VIEW_TABS.some((t) => t.key === k);

export default function EmployeeScorePage() {
  const selectedProjectId = useAuthStore((s) => s.selectedProjectId);
  const location = useLocation();
  const navigate = useNavigate();

  // Honour `?tab=…` from the URL so legacy redirects land users on the right
  // sub-tab. Default to View → Score on bare `/reports/employee-score`.
  // Legacy `?tab=quadrant` (from the old /reports/employee-quadrants redirect)
  // is folded back to 'score' since the quadrant strip now lives there.
  const initialTab = useMemo<TabKey>(() => {
    const fromUrl = new URLSearchParams(location.search).get('tab') as TabKey | null;
    const known: TabKey[] = ['score', 'quadrant', 'targets', 'trainings', 'attendance', 'seed', 'configure'];
    if (fromUrl === 'quadrant') return 'score';
    // 'metric:<id>' and legacy 'targets:<id>' shapes — pass through as-is.
    if (typeof fromUrl === 'string' && (fromUrl.startsWith('metric:') || fromUrl.startsWith('targets:'))) {
      return fromUrl as TabKey;
    }
    return fromUrl && known.includes(fromUrl) ? fromUrl : 'score';
  }, [location.search]);
  const [tab, setTabRaw] = useState<TabKey>(initialTab);
  // Keep the URL in sync as the user switches tabs so deep-link sharing works.
  const setTab = (next: TabKey) => {
    setTabRaw(next);
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
  };
  // If the URL changes externally (e.g. browser back to a redirect URL), pick
  // it up and reflect in state.
  useEffect(() => { setTabRaw(initialTab); }, [initialTab]);

  const mode: 'view' | 'manage' = isViewTab(tab) ? 'view' : 'manage';

  const [periodMonth, setPeriodMonth] = useState<string>(firstOfThisMonth());
  // Where the month came from. 'auto' = we seeded it and may replace it; 'user' = the
  // viewer picked it and we must not move it under them. An explicit flag rather than a
  // heuristic (like "is it still the current month?"), because a user who deliberately
  // picks the current month is indistinguishable from an untouched default.
  const [monthSource, setMonthSource] = useState<'auto' | 'user'>('auto');
  // Per-role landing month from the server. Fetched ONCE per project; never takes
  // periodMonth as an input, which is what keeps it from chasing its own output.
  const [defaultMonths, setDefaultMonths] = useState<{
    upto: string;
    defaultMonth: string;
    byRole: Record<string, { month: string; gated: boolean; floor: string | null }>;
  } | null>(null);
  // False until the landing month is known (or the probe failed). Gates the first score
  // fetch so a landing costs ONE project compute, not two — the mount fetch would
  // otherwise fire on the clock month and be immediately superseded.
  const [monthReady, setMonthReady] = useState(false);

  // Score + Quadrant share the same data; load once per project/month.
  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [scoreError, setScoreError] = useState<string | null>(null);
  // Metrics the engine couldn't compute this month (loader threw / timed out).
  // Not a page-level error — the rest of the scorecard is valid — so this renders
  // as an inline warning rather than replacing the table.
  const [failedMetrics, setFailedMetrics] = useState<Array<{ code: string; error?: string }>>([]);
  // Some names on screen are employee codes: the 1Office name lookup failed.
  const [namesFellBack, setNamesFellBack] = useState(false);
  // Parameters this month is still held for, by role (meta.awaiting_uploads). The
  // rows are provisional while this is non-empty — see AwaitingUploadsNotice.
  const [awaitingUploads, setAwaitingUploads] = useState<AwaitedUpload[]>([]);
  // Months whose Sales & Targets figures are waiting for an admin to say the
  // sales are in (meta.sales_feed_waiting). About the project, not the month on
  // screen, and sent only to people who may save the configuration.
  const [salesFeedWaiting, setSalesFeedWaiting] = useState<SalesFeedWaiting[]>([]);

  // View → Score sub-mode. 'monthly' = the live single-month scorecard.
  // 'sixMonth' = the last 6 monthly totals + the mean of the employee's in-tenure
  // months that have a row (pre-join / never-captured months excluded), read
  // from the monthly_scorecard_totals snapshot (ending at periodMonth).
  const [scoreView, setScoreView] = useState<'monthly' | 'sixMonth'>('monthly');
  // 6-month snapshot payload: the window months (oldest→newest) and a per-
  // employee_code lookup of { totals, avg, months_count, quadrant }. Merged into
  // the live rows by employee_code for display + role/quadrant.
  const [sixMonth, setSixMonth] = useState<{
    months: string[];
    byEmp: Map<string, { totals: Record<string, number>; avg: number | null; months_count: number; quadrant: Quadrant }>;
  } | null>(null);
  const [sixMonthLoading, setSixMonthLoading] = useState(false);

  // Configure tab state.
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [config, setConfig] = useState<ConfigEntry[]>([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  // Per-role configuration. Role selection is MANDATORY before editing —
  // the engine doesn't fall back to a project default at compute time (an
  // employee in a role without an override lands in Unclassified), so
  // configuring without choosing a role would just be a dangling template.
  // `null` is therefore "no role selected yet", not "project default".
  const [configRoleId, setConfigRoleId] = useState<string | null>(null);
  // Effective-dated configs: the Configure tab edits ONE month's version.
  // `configEffMonth` (YYYY-MM-01) is the month this config applies FROM;
  // saving writes/replaces only that month's version, and the engine scores
  // each month against the version effective for it. `configVersions` lists
  // every month that already has a saved config (for the header hint).
  const [configEffMonth, setConfigEffMonth] = useState<string>(firstOfThisMonth());
  const [configVersions, setConfigVersions] = useState<string[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  // Just the roles fetch — independent of the Configure form, used to
  // populate the dropdown even before the admin picks a role. We hit
  // getConfig without a roleId to seed the roles list, then suppress
  // catalog rendering until they pick one.
  const [rolesFetched, setRolesFetched] = useState(false);
  // Baseline snapshot of the last successfully loaded config, stringified
  // for cheap diffing. Updated inside fetchConfig() and after every save.
  // The Save changes button compares JSON.stringify(config) to this on
  // every render so it disables itself when nothing has changed.
  const configBaselineRef = useRef<string>('[]');
  // Set of role_ids that already have a saved scorecard config. Used by
  // the role-pill row to render the "Configured" checkmark on each pill.
  const [rolesWithConfig, setRolesWithConfig] = useState<Set<string>>(new Set());
  // Manage Metrics modal — opens directly into the New / Edit detail
  // screen depending on what the admin clicked. The legacy list-of-
  // metrics view inside the modal was retired in favour of per-row
  // Edit / Delete affordances on the Configure tab's Custom rows.
  // `metricsModalTarget` carries 'new' (Add Metric CTA) or the id of
  // the metric to edit (pencil click).
  const [metricsModalOpen, setMetricsModalOpen] = useState(false);
  const [metricsModalTarget, setMetricsModalTarget] = useState<'new' | number>('new');
  // Month an edit applies FROM (ManageMetricsModal `effectiveMonth`): the
  // Configure tab's version month, or a per-metric Manage tab's selected month.
  const [metricsModalMonth, setMetricsModalMonth] = useState<string | undefined>(undefined);
  // Bumped after the modal saves so an open per-metric panel reloads its
  // sub-metrics (a calculation can change from a given month onwards).
  const [metricDefsNonce, setMetricDefsNonce] = useState(0);
  const openAddMetric = () => { setMetricsModalTarget('new'); setMetricsModalMonth(undefined); setMetricsModalOpen(true); };
  const openEditMetric = (id: number, month?: string) => { setMetricsModalTarget(id); setMetricsModalMonth(month); setMetricsModalOpen(true); };
  // Inline-delete loading state — when a row's trash icon is clicked we
  // disable the button so a double-click doesn't fire two DELETEs.
  const [deletingMetricId, setDeletingMetricId] = useState<number | null>(null);
  // System metrics whose data source isn't wired up for the project yet —
  // rendered as a guidance section so the admin knows what to enable.
  const [unavailableMetrics, setUnavailableMetrics] = useState<UnavailableMetric[]>([]);
  // Project-wide "metrics included by ANY role" sets — gates the Manage
  // sub-tabs so they only appear once a metric is deliberately included
  // somewhere, not just present in the catalog.
  // Project-wide inclusion sets — currently unused after the per-role tab
  // gating below, but kept loaded so we can re-use them if the design
  // changes. Tracked separately from the per-role `config` array.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setProjectIncludedDefIds] = useState<Set<number>>(new Set());

  // Quadrant filter on the Quadrant tab (null = show all).
  const [quadFilter, setQuadFilter] = useState<Quadrant | typeof BOTTOM_BOX_KEY | null>(null);
  // View → Score dimension filters. '' = all. Combined with AND, and they also
  // narrow the quadrant-count chips so the summary matches the visible table.
  const [filterAM, setFilterAM] = useState<string>('');
  // Trade Head = the AM's manager. iPro -> AM -> Trade Head, the hierarchy the
  // Dashboard and PIP filters already use.
  const [filterTH, setFilterTH] = useState<string>('');
  // Free text over employee name / code and store name / code. A link can fill it
  // (?employeeCode=, ?storeCode=); `link` is set while it still holds what one put there.
  const [filterText, setFilterText, link] = useLinkedSearch();
  const linkRef = useRef(link);
  linkRef.current = link;
  const [filterTrade, setFilterTrade] = useState<string>('');
  const [filterCity, setFilterCity] = useState<string>('');

  // Sort state for the per-metric "Per-employee scores" table (Manage tab).
  // Shared across metric tabs; default contribution-desc preserves the old
  // "strongest on top" ordering. Click any header to re-sort (text columns
  // start A→Z, numeric ones high → low).
  const [metricSortKey, setMetricSortKey] = useState<ScoreSortKey>('contribution');
  const [metricSortDir, setMetricSortDir] = useState<'asc' | 'desc'>('desc');
  const handleMetricSort = (col: ScoreSortKey) => {
    if (col === metricSortKey) setMetricSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setMetricSortKey(col); setMetricSortDir(initialMetricScoreSortDir(col)); }
  };
  // Free-text search for the per-metric "Per-employee scores" table. Matches
  // employee name / code and store name / code (case-insensitive substring).
  // Shared across metric tabs like the sort state above.
  const [metricSearch, setMetricSearch] = useState('');

  // Build the list of metric columns shown on the Score tab. Derive them from a row
  // of the SELECTED role (when a role pill is active) — NOT the global first row.
  // rows[] spans every role, so rows[0] is whatever role ranks first; using it made
  // the table show that role's metrics as columns while the selected role's employees
  // read '—' across them (and the selected role's real metrics had no column at all).
  // Pick the first scoped row that actually has metrics; fall back to the raw first
  // row, then to config, when nothing is scored yet. Order follows config.
  const metricColumns = useMemo(() => {
    const scoped = configRoleId ? rows.filter((r) => r.role_id === configRoleId) : rows;
    const source = scoped.find((r) => (r.metrics?.length ?? 0) > 0) ?? scoped[0] ?? rows[0];
    const fromRow = source?.metrics?.map((m) => ({ code: m.code, name: m.name })) ?? [];
    if (fromRow.length > 0) return fromRow;
    return config.map((c) => ({ code: c.code, name: c.name }));
  }, [rows, configRoleId, config]);

  // Counts the score requests sent. A slow month's answer can land after a faster,
  // later one (step the month picker twice, or switch project): without this the late
  // answer replaced the rows while the picker — and the downloaded file's name — still
  // named the newer month. Only the latest request may touch the page.
  const scoreRequestRef = useRef(0);
  const sixMonthRequestRef = useRef(0);
  // The project and month the loaded rows (and the six-month window) belong to. A
  // download is only offered while these match what the picker shows, so a file can
  // never carry one month's rows under another month's name.
  const loadKey = (projectId: number | null, month: string): string => `${projectId}|${month}`;
  const [rowsLoadedFor, setRowsLoadedFor] = useState<string | null>(null);
  const [sixMonthLoadedFor, setSixMonthLoadedFor] = useState<string | null>(null);

  // View → Active | Inactive employees. The table above lists the people active
  // today; the Inactive tab lists people who have since left, with the score saved
  // for them for the month on screen (see InactiveScoresPanel). Monthly only.
  const [staffView, setStaffView] = useState<StaffView>('active');
  const [inactiveRows, setInactiveRows] = useState<InactiveScoreRow[]>([]);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [inactiveError, setInactiveError] = useState<string | null>(null);
  const [inactiveLoadedFor, setInactiveLoadedFor] = useState<string | null>(null);
  const [inactiveSearch, setInactiveSearch] = useState('');
  const inactiveRequestRef = useRef(0);

  const fetchInactive = useCallback(async () => {
    if (!selectedProjectId || !monthReady) return;
    const request = ++inactiveRequestRef.current;
    setInactiveLoading(true);
    setInactiveError(null);
    try {
      const res = await scorecardAPI.getInactiveScores(selectedProjectId, periodMonth);
      if (request !== inactiveRequestRef.current) return;
      setInactiveRows(Array.isArray(res?.data?.data) ? res.data.data : []);
      setInactiveLoadedFor(loadKey(selectedProjectId, periodMonth));
    } catch (err: any) {
      if (request !== inactiveRequestRef.current) return;
      setInactiveError(err?.response?.data?.message || 'Could not load the employees who have left.');
      setInactiveRows([]);
      setInactiveLoadedFor(null);
    } finally {
      if (request === inactiveRequestRef.current) setInactiveLoading(false);
    }
  // loadKey is a plain function of its arguments.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId, periodMonth, monthReady]);

  const fetchScore = useCallback(async () => {
    // Hold the first fetch until the landing month is resolved. Without this the mount
    // fires on the browser's clock month and is superseded milliseconds later — two
    // full project computes for one page load, and a visible flash of the wrong month.
    if (!selectedProjectId || !monthReady) return;
    const request = ++scoreRequestRef.current;
    setScoreLoading(true);
    setScoreError(null);
    try {
      const res = await scorecardAPI.getScore(selectedProjectId, periodMonth);
      if (request !== scoreRequestRef.current) return;
      setRows(res?.data?.data || []);
      setRowsLoadedFor(loadKey(selectedProjectId, periodMonth));
      // A metric whose loader threw is isolated by the engine — the rest of the
      // scorecard still computes, and that metric's column is simply left blank.
      // Without surfacing this, the failure is invisible: the metric silently
      // vanishes from every row, and the per-metric tab then tells the admin to
      // "tick this metric for a role and save" — work they have already done.
      const failed: Array<{ code: string; error?: string }> = Array.isArray(res?.data?.meta?.failed_metrics)
        ? res.data.meta.failed_metrics
        : [];
      setFailedMetrics(failed.filter((f) => f.code !== NAME_LOOKUP_FAILED_CODE));
      setNamesFellBack(failed.some((f) => f.code === NAME_LOOKUP_FAILED_CODE));
      const awaiting = res?.data?.meta?.awaiting_uploads;
      setAwaitingUploads(Array.isArray(awaiting) ? awaiting : []);
      const feedWaiting = res?.data?.meta?.sales_feed_waiting;
      setSalesFeedWaiting(Array.isArray(feedWaiting) ? feedWaiting : []);
    } catch (err: any) {
      if (request !== scoreRequestRef.current) return;
      setScoreError(err?.response?.data?.message || err?.message || 'Failed to load scorecard');
      setRows([]);
      setRowsLoadedFor(null);
      setFailedMetrics([]);
      setNamesFellBack(false);
      setAwaitingUploads([]);
      // Never left standing from the answer before: after a project switch it
      // would offer the other project's months.
      setSalesFeedWaiting([]);
    } finally {
      // A superseded request leaves "loading" to the one that replaced it.
      if (request === scoreRequestRef.current) setScoreLoading(false);
    }
  }, [selectedProjectId, periodMonth, monthReady]);

  // 6-month snapshot fetch — only fired when the 6-month view is active. Pivots
  // the employees[] response into a Map for O(1) merge-by-code against rows.
  const fetchSixMonth = useCallback(async () => {
    if (!selectedProjectId) return;
    const request = ++sixMonthRequestRef.current;
    setSixMonthLoading(true);
    try {
      const res = await scorecardAPI.getSixMonthScore(selectedProjectId, periodMonth);
      if (request !== sixMonthRequestRef.current) return;
      const data = res?.data?.data || {};
      const months: string[] = Array.isArray(data.months) ? data.months : [];
      const byEmp = new Map<string, { totals: Record<string, number>; avg: number | null; months_count: number; quadrant: Quadrant }>();
      for (const e of (data.employees || []) as any[]) {
        byEmp.set(String(e.employee_code), {
          totals: e.totals || {},
          avg: e.avg === null || e.avg === undefined ? null : Number(e.avg),
          months_count: Number(e.months_count) || 0,
          quadrant: (e.quadrant as Quadrant) || 'Unclassified',
        });
      }
      setSixMonth({ months, byEmp });
      setSixMonthLoadedFor(loadKey(selectedProjectId, periodMonth));
    } catch (err: any) {
      if (request !== sixMonthRequestRef.current) return;
      setScoreError(err?.response?.data?.message || err?.message || 'Failed to load 6-month scores');
      setSixMonth(null);
      setSixMonthLoadedFor(null);
    } finally {
      if (request === sixMonthRequestRef.current) setSixMonthLoading(false);
    }
  }, [selectedProjectId, periodMonth]);

  // Two-mode fetch:
  //  - Without configRoleId: load just enough to populate the role dropdown +
  //    the project's catalog availability info. Catalog/config arrays are left
  //    empty so the Configure form stays in its "pick a role" state.
  //  - With configRoleId: load that role's catalog + config (the actual
  //    metrics, weights, slabs to edit).
  const fetchConfig = useCallback(async () => {
    if (!selectedProjectId) return;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const res = await scorecardAPI.getConfig(selectedProjectId, configRoleId, configEffMonth);
      const data = res?.data?.data || {};
      // Months that already have a saved config for this role — drives the
      // "existing versions" hint next to the month selector.
      setConfigVersions(Array.isArray(data.versions) ? data.versions.map((x: any) => String(x)) : []);
      // Always refresh the availability list (project-level signal).
      setUnavailableMetrics(data.available_once_captured || []);
      // Refresh the project-wide inclusion set — drives Manage sub-tab
      // visibility regardless of which role is currently selected.
      setProjectIncludedDefIds(new Set<number>((data.project_included_metric_def_ids || []).map((n: any) => Number(n))));

      // Roles: prefer the inline list the scorecard endpoint returns. Fall
      // back to /roles/project/:id so the dropdown still populates against
      // an older backend (or any environment where the scorecard endpoint
      // hasn't picked up the inline-roles change yet).
      const inlineRoles: RoleOption[] = data.roles || [];
      if (inlineRoles.length > 0) {
        setRoleOptions(inlineRoles);
      } else {
        try {
          const r = await rolesAPI.getByProject(selectedProjectId);
          // The shape from /roles/project/:id is { data: [{ id, name, ... }] }.
          const arr: any[] = r?.data?.data ?? r?.data ?? [];
          setRoleOptions(arr.map((x: any) => ({
            id: String(x.id),
            name: String(x.name || ''),
            user_count: Number(x.user_count) || 0,
          })));
        } catch {
          setRoleOptions([]);
        }
      }
      // Track which roles already carry a saved scorecard — the pill row
      // uses this to render a "Configured" checkmark on each pill, and the
      // auto-select below honours it on first visit.
      const rolesWithCfgList: string[] = Array.isArray(data.roles_with_config)
        ? data.roles_with_config.map((x: any) => String(x))
        : [];
      setRolesWithConfig(new Set(rolesWithCfgList));

      // Auto-select a role on first load when the project already has at
      // least one configured role — saves the admin a click on every visit.
      // We only pre-select when the chosen role ID is ALSO present in the
      // available roles list; otherwise we'd land on a phantom UUID with no
      // matching pill (the bug you spotted with the raw UUID showing up).
      // Not for a link: the rows are cut to the picked role, and the first configured
      // role need not be the linked person's (see the effect under roleScopedRows).
      if (configRoleId === null && !rolesFetched && rolesWithCfgList.length > 0 && !linkRef.current) {
        const finalRoles = inlineRoles.length > 0 ? inlineRoles : roleOptions;
        const ids = new Set(finalRoles.map((r) => r.id));
        const preselect = rolesWithCfgList.find((id) => ids.has(id));
        if (preselect) {
          // Defer the state update so this fetch completes cleanly; the
          // role change will fire its own fetch via the useEffect chain.
          setTimeout(() => setConfigRoleId(preselect), 0);
        }
      }
      setRolesFetched(true);
      // Catalog is always populated — it's project-level and the Manage sub-tab
      // visibility uses it for "does the project have any target metrics?"
      // The PER-ROLE config is cleared when no role is selected so we don't
      // render half-empty editor state.
      setCatalog(data.catalog || []);
      // Normalise `status` so a missing field (older API build that
      // pre-dates the pause/resume work) is treated as 'active'. Without
      // this guard, every saved entry would render as unchecked on the
      // Configure tab because `cfg.status === 'active'` evaluates against
      // undefined.
      const loadedConfig: ConfigEntry[] = configRoleId
        ? (data.config || []).map((c: any): ConfigEntry => ({
            ...c,
            status: c.status === 'inactive' ? 'inactive' : 'active',
          }))
        : [];
      setConfig(loadedConfig);
      // Snapshot the loaded state so the Save changes button can disable
      // itself when there are no edits to send. Stringified for cheap
      // O(n) diff on every render (config is small — handful of rows).
      configBaselineRef.current = JSON.stringify(loadedConfig);
    } catch (err: any) {
      setConfigError(err?.response?.data?.message || err?.message || 'Failed to load config');
    } finally {
      setConfigLoading(false);
    }
  }, [selectedProjectId, configRoleId, configEffMonth]);

  useEffect(() => { fetchScore(); }, [fetchScore]);
  // Loaded with the View tab, not on first click, so its pill can carry a count.
  useEffect(() => { if (tab === 'score') fetchInactive(); }, [tab, fetchInactive]);
  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  // Load the 6-month snapshot only while that view is active (and re-load when
  // the project/month moves the window). The live monthly rows keep loading via
  // fetchScore above — the 6-month view merges onto them for meta + role.
  useEffect(() => {
    if (scoreView === 'sixMonth') fetchSixMonth();
  }, [scoreView, fetchSixMonth]);

  // ── Landing month ───────────────────────────────────────────────────────────
  // Fetched ONCE per project. Deliberately takes no month argument: if the request
  // depended on periodMonth, applying the answer would re-fire it and the two would
  // chase each other. Fails OPEN — on error we mark ready and keep the clock month,
  // i.e. exactly the old behaviour.
  useEffect(() => {
    let cancelled = false;
    if (!selectedProjectId) { setMonthReady(true); return; }
    setMonthReady(false);
    scorecardAPI.getDefaultMonths(selectedProjectId)
      .then((res) => { if (!cancelled) setDefaultMonths(res?.data?.data ?? null); })
      .catch(() => { if (!cancelled) setDefaultMonths(null); })
      .finally(() => { if (!cancelled) setMonthReady(true); });
    return () => { cancelled = true; };
  }, [selectedProjectId]);

  // Apply the landing month for the role in view.
  //
  // Three guards, each load-bearing:
  //  • monthSource === 'auto' — once the viewer picks a month, we never move it. They
  //    get an inline "latest scored month is X" offer instead of silent movement.
  //  • mode === 'view' — periodMonth is SHARED with the Manage/upload tabs, where it
  //    decides which month values are keyed into. Retargeting an admin's upload month
  //    is the worst failure this feature could cause, so Manage keeps what it has.
  //  • periodMonth NOT in the deps — writing it here must not re-run this effect.
  useEffect(() => {
    if (!defaultMonths || monthSource !== 'auto' || mode !== 'view') return;
    const target = (configRoleId && defaultMonths.byRole[configRoleId]?.month)
      || defaultMonths.defaultMonth;
    if (target) setPeriodMonth(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMonths, configRoleId, monthSource, mode]);

  // Project-change reset. When the admin switches projects, drop any stale
  // role selection / role list / config / baseline so the next fetchConfig
  // starts clean. Without this, configRoleId held a UUID from the previous
  // project, which the backend rejected on save with
  // "role_id … does not belong to project N".
  const lastProjectRef = useRef<number | null>(null);
  useEffect(() => {
    if (lastProjectRef.current !== null && lastProjectRef.current !== selectedProjectId) {
      setConfigRoleId(null);
      setConfig([]);
      setConfigEffMonth(firstOfThisMonth());
      // The new project has its own landing month — re-seed rather than carry the
      // previous project's month (or the user's choice within it) across.
      setMonthSource('auto');
      setDefaultMonths(null);
      setMonthReady(false);
      setConfigVersions([]);
      setRoleOptions([]);
      setRolesFetched(false);
      setRolesWithConfig(new Set());
      setSaveMsg(null);
      configBaselineRef.current = '[]';
    }
    lastProjectRef.current = selectedProjectId ?? null;
  }, [selectedProjectId]);

  // Live monthly rows enriched with 6-month snapshot data (totals/avg) and, in the
  // 6-month view, the backend's role-grouped 6-month quadrant (six.quadrant) — the
  // SAME value the Dashboard "Standing" reads, so the two never disagree. In
  // monthly mode rows pass through untouched (quadrant = the monthly role quadrant).
  const mergedRows = useMemo<MergedRow[]>(() => {
    // In BOTH views, a fresh joiner (< 6 months) who'd read 'Unclassified' (no rankable
    // data yet) is surfaced as 'New Joiner' — matching the mobile app so the two agree.
    if (scoreView !== 'sixMonth') {
      return rows.map((r) => ({ ...r, six: null, quadrant: withNewJoinerQuadrant(r.quadrant, r.date_of_joining) }));
    }
    const byEmp = sixMonth?.byEmp;
    // In the 6-month view, override each row's quadrant with the backend's
    // role-grouped 6-month quadrant (computeSixMonthScores) — the SAME value the
    // Dashboard "Standing" reads. A row with no snapshot entry (six null) has no
    // in-tenure data → 'Unclassified', then upgraded to 'New Joiner' if recently joined.
    return rows.map((r) => {
      const six = byEmp?.get(r.employee_code) ?? null;
      return { ...r, six, quadrant: withNewJoinerQuadrant(six?.quadrant ?? 'Unclassified', r.date_of_joining) };
    });
  }, [rows, scoreView, sixMonth]);

  // Role filter — when a role pill is selected on the View tab, show
  // only employees whose engine-resolved role matches it. Without this,
  // selecting "Trade Heads" still surfaces every employee in the
  // project. configRoleId is shared with Manage/Configure so the same
  // pill drives both surfaces.
  const roleScopedRows = useMemo(
    () => (configRoleId ? mergedRows.filter((r) => r.role_id === configRoleId) : mergedRows),
    [mergedRows, configRoleId],
  );

  // A link names a person or a store, and the cut above would hide them behind whichever
  // role is picked. So each link moves the pick ONCE, when the month's rows and the role
  // pills are in: to the one role its rows hold (the table then shows that role's own
  // parameters), or to no role when they hold several (the people of a store) or a role
  // with no pill. A link whose rows are not in this month leaves the pick alone. After
  // that the pick is the person's again, and stays where they put it.
  const roleMovedFor = useRef<SearchLink | null>(null);
  useEffect(() => {
    if (!link || roleMovedFor.current === link) return;
    if (scoreLoading || rowsLoadedFor === null || !rolesFetched) return;
    roleMovedFor.current = link;
    const q = link.text.trim().toLowerCase();
    const roles = new Set(rows.filter((r) => scoreRowMatchesText(r, q)).map((r) => r.role_id));
    if (roles.size === 0) return;
    const [only] = roles;
    setConfigRoleId(roles.size === 1 && roleOptions.some((r) => r.id === only) ? only : null);
  }, [link, rows, scoreLoading, rowsLoadedFor, rolesFetched, roleOptions]);

  // Role rosters for the Manage-tab Value grids. When a role is selected, the
  // per-metric data-entry tables (inside SubMetricsManagePanel) scope their
  // subject list to these — employees by code, stores to those that hold at
  // least one role employee. Both null when no role is selected (show all).
  // Derived from the role-aware scorecard rows, whose role_id the backend
  // resolves from user_roles, so they match the backend template filter.
  const roleStoreCodes = useMemo<Set<string> | null>(
    () => (configRoleId
      ? new Set(roleScopedRows.map((r) => r.store_code).filter((c): c is string => !!c))
      : null),
    [roleScopedRows, configRoleId],
  );

  // Dimension-filter option lists (AM / Store Trade / City) — derived from the
  // role-scoped rows so the choices stay stable as you select. Sorted, blanks
  // dropped.
  const uniqSorted = (vals: (string | undefined)[]): Array<{ value: string; label: string }> =>
    Array.from(new Set(vals.filter((v): v is string => !!v && v.trim() !== '')))
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));
  // Area Manager options are keyed by manager CODE (value) with the resolved name as
  // the label — so the filter matches the reporting-manager code (robust) even when the
  // manager's NAME didn't resolve for some rows. Dedupe by code; keep the first non-empty
  // name seen for that code (fall back to the code itself if no name ever resolves).
  const amOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    const byCode = new Map<string, string>();
    for (const r of roleScopedRows) {
      // Narrow to the selected Trade Head's AMs. An AM belongs to exactly ONE Trade
      // Head, so an AM+TH pair from different branches isn't a "no results" query, it's
      // a nonsense one — and independent dropdowns would render it as a blank table with
      // no explanation. Deliberately ONE-directional: picking an AM does not collapse
      // the Trade Head list to a single entry.
      if (filterTH && (r.th_code || '').trim() !== filterTH) continue;
      const code = (r.manager_code || '').trim();
      if (!code) continue;
      const name = (r.area_manager || '').trim();
      if (!byCode.has(code) || (name && !byCode.get(code))) byCode.set(code, name);
    }
    return [...byCode.entries()]
      .map(([value, name]) => ({ value, label: name || value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [roleScopedRows, filterTH]);
  // Trade Head options — same shape as amOptions, keyed on the second hop.
  const thOptions = useMemo<Array<{ value: string; label: string }>>(() => {
    const byCode = new Map<string, string>();
    for (const r of roleScopedRows) {
      const code = (r.th_code || '').trim();
      if (!code) continue;
      const name = (r.th_name || '').trim();
      if (!byCode.has(code) || (name && !byCode.get(code))) byCode.set(code, name);
    }
    return [...byCode.entries()]
      .map(([value, name]) => ({ value, label: name || value }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [roleScopedRows]);
  const tradeOptions = useMemo(() => uniqSorted(roleScopedRows.map((r) => r.store_trade)), [roleScopedRows]);
  const cityOptions = useMemo(() => uniqSorted(roleScopedRows.map((r) => r.city)), [roleScopedRows]);

  // Apply the AM/Trade/City dropdowns (AND) before the quadrant split, so both
  // the quadrant chips and the table reflect the same filtered set.
  // Free text is folded in HERE rather than into a later memo, so the quadrant chips,
  // the Bottom Box card and the "N employees" counter all track the search too —
  // everything downstream reads dimFilteredRows.
  const dimFilteredRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    return roleScopedRows.filter(
      (r) =>
        (!filterTH || (r.th_code || '').trim() === filterTH) &&
        (!filterAM || (r.manager_code || '').trim() === filterAM) &&
        (!filterTrade || r.store_trade === filterTrade) &&
        (!filterCity || r.city === filterCity) &&
        (!q || scoreRowMatchesText(r, q)),
    );
  }, [roleScopedRows, filterTH, filterAM, filterTrade, filterCity, filterText]);

  // ── Quadrant tab ─────────────────────────────────────────────────────────
  const quadrantCounts = useMemo(() => {
    const c: Record<Quadrant, number> = {
      'Stars': 0, 'Achievers': 0, 'Strivers': 0, 'At Risk': 0, 'Unclassified': 0, 'New Joiner': 0, 'Excluded': 0,
    };
    // Counts reflect the dimension-filtered view the table renders.
    for (const r of dimFilteredRows) c[r.quadrant]++;
    return c;
  }, [dimFilteredRows]);
  // Bottom Box count is separate from quadrantCounts because it's an OVERLAPPING
  // subset (these employees are also counted under At Risk), not its own quadrant.
  const bottomBoxCount = useMemo(
    () => dimFilteredRows.reduce((n, r) => n + (r.is_bottom_box ? 1 : 0), 0),
    [dimFilteredRows],
  );
  const filteredRows = useMemo(
    () =>
      !quadFilter
        ? dimFilteredRows
        : quadFilter === BOTTOM_BOX_KEY
          ? dimFilteredRows.filter((r) => r.is_bottom_box)
          : dimFilteredRows.filter((r) => r.quadrant === quadFilter),
    [dimFilteredRows, quadFilter],
  );

  // ── Rank (within reporting manager) + AON ──────────────────────────────────
  // Rank mirrors the mobile leaderboard: an employee's position among everyone under
  // their SAME direct manager. Monthly uses the backend's manager_rank (authoritative —
  // the exact badge the app shows). The 6-month view has no per-manager 6-month rank
  // from the API, so we rank client-side by the 6-month AVERAGE using the identical
  // order (score desc, earlier joiner wins ties, then name/code) + competition ranking
  // ("1224", ties on the rounded score share a rank). Computed over the FULL loaded
  // roster so the AM / quadrant / trade filters never change anyone's rank.
  const cmpStanding = (a: MergedRow, b: MergedRow, scoreOf: (r: MergedRow) => number): number => {
    const sa = scoreOf(a);
    const sb = scoreOf(b);
    if (sb !== sa) return sb - sa;
    const at = a.date_of_joining ? new Date(a.date_of_joining).getTime() : NaN;
    const bt = b.date_of_joining ? new Date(b.date_of_joining).getTime() : NaN;
    const aK = !Number.isNaN(at);
    const bK = !Number.isNaN(bt);
    if (aK && bK && at !== bt) return at - bt; // earlier joiner ranks first
    if (aK !== bK) return aK ? -1 : 1;
    return (a.employee_name || '').localeCompare(b.employee_name || '')
      || a.employee_code.localeCompare(b.employee_code);
  };
  const sixMonthRankByEmp = useMemo(() => {
    const map = new Map<string, { rank: number; total: number }>();
    if (scoreView !== 'sixMonth') return map;
    const byMgr = new Map<string, MergedRow[]>();
    for (const r of mergedRows) {
      const mgr = r.manager_code ? String(r.manager_code).trim() : '';
      if (!mgr || r.six?.avg == null) continue; // no manager or no 6-month score → unranked
      const list = byMgr.get(mgr) ?? [];
      list.push(r);
      byMgr.set(mgr, list);
    }
    byMgr.forEach((peers) => {
      peers.sort((a, b) => cmpStanding(a, b, (r) => r.six?.avg ?? 0));
      peers.forEach((r, i) => {
        const s = Math.round(r.six?.avg ?? 0);
        const rank = i > 0 && s === Math.round(peers[i - 1].six?.avg ?? 0)
          ? map.get(peers[i - 1].employee_code)!.rank // tie on the rounded score → share rank
          : i + 1;
        map.set(r.employee_code, { rank, total: peers.length });
      });
    });
    return map;
  }, [mergedRows, scoreView]);
  const rankOf = (r: MergedRow): { rank: number; total: number } | null => {
    if (scoreView === 'sixMonth') return sixMonthRankByEmp.get(r.employee_code) ?? null;
    return r.manager_rank != null && r.manager_rank_total != null
      ? { rank: r.manager_rank, total: r.manager_rank_total }
      : null;
  };
  // AON = current tenure (as of today), independent of the viewed month — see
  // aonMonthsToday. A recent joiner shows their tenure even in an earlier month.
  const aonMonthsOf = aonMonthsToday;
  const aonLabel = aonLabelFrom;

  // View → Score table sort. Key is a metric `code`, '__total__', or null
  // (null = the API's default ranked order). Numeric columns sort on the
  // weighted contribution (the value the cell now shows); a row missing the
  // metric sinks to the bottom regardless of direction.
  const [viewSortKey, setViewSortKey] = useState<string | null>(null);
  const [viewSortDir, setViewSortDir] = useState<'asc' | 'desc'>('desc');
  const handleViewSort = (key: string) => {
    if (key === viewSortKey) setViewSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    // Rank reads best-first (1, 2, 3…) and the text columns (name, code, store,
    // quadrant) read A→Z, so those default to ascending; everything else (scores,
    // tenure) reads strongest/longest-first, so defaults to descending.
    else { setViewSortKey(key); setViewSortDir(key === '__rank__' || SCORE_TEXT_SORT_KEYS.has(key) ? 'asc' : 'desc'); }
  };
  const sortedRows = useMemo(() => {
    if (!viewSortKey) return filteredRows;
    const dir = viewSortDir === 'asc' ? 1 : -1;
    // Sort keys: '__total__' (monthly total), a metric `code` (monthly
    // contribution), '__avg__' (6-month average), or 'm:YYYY-MM-01' (a single
    // snapshot month). A row missing the keyed value sinks to the bottom.
    // Text columns (name / code / store / quadrant) sort on the text the cell
    // shows; a blank one ('—' in the cell) sinks like any missing value.
    const textOrNull = (v: string | null | undefined): string | null => (v && v.trim() ? v : null);
    const valueOf = (r: MergedRow): number | string | null => {
      if (viewSortKey === '__avg__') return r.six?.avg ?? null;
      if (viewSortKey.startsWith('m:')) {
        const v = r.six?.totals[viewSortKey.slice(2)];
        return v === undefined ? null : v;
      }
      // Pre-join rows show "—" (not scored) — treat as missing so they sink to the
      // bottom on either sort direction, consistent with a missing metric column,
      // rather than sorting as a literal 0 (which would top an ascending sort).
      if (viewSortKey === '__total__') return r.preJoin ? null : r.total;
      if (viewSortKey === '__rank__') return rankOf(r)?.rank ?? null;
      if (viewSortKey === '__aon__') return aonMonthsOf(r.date_of_joining);
      if (viewSortKey === '__emp_name__') return textOrNull(r.employee_name);
      if (viewSortKey === '__emp_code__') return textOrNull(r.employee_code);
      if (viewSortKey === '__store_name__') return textOrNull(r.store_name);
      if (viewSortKey === '__store_code__') return textOrNull(r.store_code);
      if (viewSortKey === '__store_address__') return textOrNull(r.store_address);
      // The quadrant chip, then the Bottom Box chip beside it when shown — so
      // Bottom Box rows cluster together at the edge of At Risk.
      if (viewSortKey === '__quadrant__') return r.is_bottom_box ? `${r.quadrant} Bottom Box` : r.quadrant;
      return r.metrics.find((m) => m.code === viewSortKey)?.contribution ?? null;
    };
    return [...filteredRows].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [filteredRows, viewSortKey, viewSortDir, sixMonthRankByEmp, scoreView, periodMonth]);

  // Switching the score view resets the sort: the 6-month view defaults to the
  // Average column (strongest sustained on top), monthly restores the API's
  // default ranked order. Avoids a stale metric/month key carrying across.
  useEffect(() => {
    if (scoreView === 'sixMonth') { setViewSortKey('__avg__'); setViewSortDir('desc'); }
    else { setViewSortKey(null); setViewSortDir('desc'); }
  }, [scoreView]);

  // Manage sub-tab visibility — gated on the metrics the CURRENT role has
  // saved in its scorecard config. We don't want a tab for "Grooming" to
  // appear when the active role hasn't ticked Grooming, even if some other
  // role in the project did. `config` is the active role's draft (loaded
  // by fetchConfig whenever configRoleId changes); reading from it keeps
  // the tabs in step with the role-switcher.
  const includedMetrics = useMemo(() => {
    // Only metrics whose config entry is currently ACTIVE get a Manage
    // sub-tab. Paused entries (status='inactive' — kept around so their
    // weight + slabs survive uncheck/recheck on Configure) are not part
    // of the role's scorecard right now and shouldn't surface here either.
    const includedDefIds = new Set<number>(
      config.filter((c) => c.status === 'active').map((c) => c.metric_def_id),
    );
    return catalog
      .filter((c) => includedDefIds.has(c.metric_def_id))
      // Stable order: system first, then task_question, then target_metric.
      // Within each group, alphabetical so the tab strip stays predictable.
      .slice()
      .sort((a, b) => {
        const kindOrder = { system: 0, task_question: 1, target_metric: 2 } as const;
        const kdiff = kindOrder[a.kind] - kindOrder[b.kind];
        if (kdiff !== 0) return kdiff;
        return a.name.localeCompare(b.name);
      });
  }, [catalog, config]);

  const visibleManageTabs = useMemo<{ key: TabKey; label: string }[]>(() => {
    const tabs: { key: TabKey; label: string }[] = [];
    for (const m of includedMetrics) {
      tabs.push({
        key: `metric:${m.metric_def_id}` as TabKey,
        label: m.name,
      });
    }
    tabs.push({ key: 'configure', label: 'Configure' });
    return tabs;
  }, [includedMetrics]);

  // Active per-metric sub-tab. Accepts the new 'metric:<id>' shape AND the
  // legacy 'targets:<id>' shape (back-compat with bookmarks from earlier
  // builds — they resolved to the same metric in those cases anyway).
  const activeMetricTab = useMemo<CatalogEntry | null>(() => {
    if (typeof tab !== 'string') return null;
    let id: number | null = null;
    if (tab.startsWith('metric:')) id = Number(tab.slice('metric:'.length));
    else if (tab.startsWith('targets:')) id = Number(tab.slice('targets:'.length));
    if (id === null || !Number.isFinite(id)) return null;
    return catalog.find((c) => c.metric_def_id === id) ?? null;
  }, [tab, catalog]);

  // ── Manage → per-metric "Per-employee scores" rows ───────────────────────
  // For the kinds that render the inline scores card (everything except
  // target_metric, whose panel carries its own). Computed here rather than in
  // the JSX so the card and the Download button read the same rows.
  const inlineScoresMetricCode =
    activeMetricTab && activeMetricTab.kind !== 'target_metric' ? activeMetricTab.code : null;
  // Every loaded row projected to just this metric; employees with no entry
  // for it are dropped (they're outside the rosters that include the metric).
  const metricTabScores = useMemo<{ rows: PerEmployeeMetricScoreRow[]; weightPct: number }>(() => {
    if (inlineScoresMetricCode === null) return { rows: [], weightPct: 0 };
    const projected = rows
      .map((r) => ({
        employee_code: r.employee_code,
        employee_name: r.employee_name,
        store_name: r.store_name,
        store_code: r.store_code,
        store_address: r.store_address,
        m: r.metrics.find((x) => x.code === inlineScoresMetricCode) ?? null,
      }))
      .filter((r) => r.m !== null);
    return {
      rows: projected.map((r) => ({
        employee_code: r.employee_code,
        employee_name: r.employee_name,
        store_name: r.store_name,
        store_code: r.store_code,
        store_address: r.store_address,
        value: r.m!.value,
        points: r.m!.points,
        contribution: r.m!.contribution,
      })),
      weightPct: projected[0]?.m?.weight_pct ?? 0,
    };
  }, [rows, inlineScoresMetricCode]);
  // Sorted by the admin-selected column (default contribution desc — strongest
  // performers on top; blank values sink to the bottom regardless of direction),
  // then narrowed by the free-text search (employee name/code, store name/code).
  // Empty query → show everyone.
  const metricTabVisibleRows = useMemo(() => {
    const sorted = [...metricTabScores.rows]
      .sort((a, b) => compareMetricScoreRows(a, b, metricSortKey, metricSortDir));
    const q = metricSearch.trim().toLowerCase();
    return q
      ? sorted.filter((r) =>
          `${r.employee_name ?? ''} ${r.employee_code ?? ''} ${r.store_name ?? ''} ${r.store_code ?? ''}`
            .toLowerCase()
            .includes(q),
        )
      : sorted;
  }, [metricTabScores, metricSortKey, metricSortDir, metricSearch]);

  // ── Download ─────────────────────────────────────────────────────────────
  // One button, one rule: the Excel file is the table on screen — the same
  // columns in the same order and the same rows in the same order, so the role
  // pill, filters, search and column sort all carry into the file. The workbook
  // is built in the browser from the rows already rendered; nothing is refetched.
  //
  // A target_metric tab's tables live inside SubMetricsManagePanel, so the panel
  // hands its open inner tab over through this ref and says whether it has rows.
  const panelExportRef = useRef<TableSheetGetter | null>(null);
  const [panelExportable, setPanelExportable] = useState(false);
  // Same test the render below uses: without a target_metric_id the tab shows the
  // legacy Targets page instead of the panel, and that has no table to hand over.
  const panelOnScreen =
    activeMetricTab?.kind === 'target_metric' && activeMetricTab.target_metric_id != null;
  // Where the button is offered: View → Score, and every Manage parameter tab that
  // has a table of its own. The legacy Targets page shown in place of a panel has
  // none, and a button that could never be enabled only invites clicks.
  const downloadOffered =
    tab === 'score'
    || (mode === 'manage' && !!activeMetricTab && (activeMetricTab.kind !== 'target_metric' || panelOnScreen));
  // The panel only reports while it is open. Once another tab replaces it nothing
  // would correct a leftover "has rows", so drop what it left behind.
  useEffect(() => {
    if (panelOnScreen) return;
    panelExportRef.current = null;
    setPanelExportable(false);
  }, [panelOnScreen]);

  const [downloading, setDownloading] = useState(false);
  // Kept apart from scoreError on purpose: that one replaces the table with its
  // message, and a failed download must leave the table where it is.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // The message is about one table. Anything that puts a different table on screen
  // clears it: another tab or view, another month or role, or the panel's inner tab
  // (which is when panelExportable flips).
  useEffect(() => { setDownloadError(null); }, [tab, scoreView, staffView, periodMonth, configRoleId, panelExportable]);
  // What the button was pointing at when it was clicked, so a failure that arrives
  // after the user has moved on is not reported over a table that never failed.
  const downloadContext = `${tab}|${scoreView}|${staffView}|${periodMonth}|${configRoleId ?? ''}`;
  const downloadContextRef = useRef(downloadContext);
  downloadContextRef.current = downloadContext;

  // True when the table on screen has at least one row to write. While a table
  // is loading, or an error / empty message stands in its place, there is none.
  const onScreenKey = loadKey(selectedProjectId ?? null, periodMonth);
  const scoresReady = !scoreLoading && !scoreError && rowsLoadedFor === onScreenKey;
  // The Inactive tab's rows, only once they are the ones for the month on screen.
  const inactiveReady = !inactiveLoading && !inactiveError && inactiveLoadedFor === onScreenKey;
  const inactiveOnScreen = inactiveReady ? inactiveRows : [];
  const inactiveVisible = visibleInactiveRows(inactiveOnScreen, configRoleId, inactiveSearch);
  const showingInactive = tab === 'score' && staffView === 'inactive';
  const canDownload =
    showingInactive
      ? inactiveReady && inactiveVisible.length > 0
      : tab === 'score'
      ? scoresReady && sortedRows.length > 0
        && (scoreView === 'monthly' || (!sixMonthLoading && sixMonth !== null && sixMonthLoadedFor === onScreenKey))
      : !activeMetricTab
        ? false
        : activeMetricTab.kind === 'target_metric'
          ? panelOnScreen && panelExportable
          : scoresReady && metricTabVisibleRows.length > 0;

  // The sheet for whatever is on screen, and the file name it is saved under.
  const sheetOnScreen = (): { filename: string; sheet: TableSheet } | null => {
    const suffix = `p${selectedProjectId}_${periodMonth.slice(0, 7)}.xlsx`;
    if (tab === 'score') {
      // The View table is the selected role's, so its files carry the role: one
      // downloaded per role would otherwise all share a name.
      const roleSlug = fileSlug(roleOptions.find((r) => r.id === configRoleId)?.name ?? '');
      const role = roleSlug ? `${roleSlug}_` : '';
      if (staffView === 'inactive') {
        return { filename: `employee_score_inactive_${role}${suffix}`, sheet: buildInactiveScoreSheet(inactiveVisible) };
      }
      if (scoreView === 'sixMonth') {
        if (!sixMonth) return null;
        return {
          filename: `employee_6month_score_${role}${suffix}`,
          sheet: buildSixMonthScoreSheet({
            rows: sortedRows, months: sixMonth.months, monthLabel: fmtMonthShort, rankOf, aonLabel,
          }),
        };
      }
      return {
        filename: `employee_score_${role}${suffix}`,
        sheet: buildMonthlyScoreSheet({ rows: sortedRows, metricColumns, rankOf, aonLabel }),
      };
    }
    if (!activeMetricTab) return null;
    const metricSlug = fileSlug(activeMetricTab.name) || 'parameter';
    if (activeMetricTab.kind !== 'target_metric') {
      return {
        filename: `employee_score_${metricSlug}_${suffix}`,
        sheet: metricScoreSheet(activeMetricTab.name, metricTabVisibleRows, metricTabScores.weightPct),
      };
    }
    // The panel has several inner tabs; the inner tab's name goes in the file
    // name so two downloads from the same parameter don't overwrite each other.
    const sheet = panelExportRef.current?.() ?? null;
    return sheet
      ? { filename: `employee_score_${metricSlug}_${fileSlug(sheet.name) || 'table'}_${suffix}`, sheet }
      : null;
  };

  const handleDownload = async () => {
    // `downloading` also disables the button; this covers a click already on its way.
    if (downloading || !canDownload) return;
    const clickedOn = downloadContext;
    setDownloading(true);
    setDownloadError(null);
    try {
      const target = sheetOnScreen();
      if (!target || target.sheet.rows.length === 0) {
        // The panel's table can empty between its last report and the click.
        setDownloadError('There are no rows on screen to download.');
        return;
      }
      await downloadTableXlsx(target.filename, target.sheet);
    } catch {
      if (downloadContextRef.current === clickedOn) {
        setDownloadError('Could not build the Excel file. Please try again; if it keeps failing, reload the page.');
      }
    } finally {
      setDownloading(false);
    }
  };

  // ── Configure tab ────────────────────────────────────────────────────────
  // The Configure tab works on a local draft of the config, keyed by
  // metric_def_id; saving sends the draft as the new state.
  const includedById = useMemo(() => {
    const m = new Map<number, ConfigEntry>();
    for (const c of config) m.set(c.metric_def_id, c);
    return m;
  }, [config]);

  // Total weight reflects ONLY the active entries — inactive (paused)
  // entries are kept in state to preserve their slabs/weight across
  // uncheck → save → re-check, but they shouldn't drag the visible
  // total away from 100% while paused.
  const weightTotal = useMemo(
    () => Math.round(
      config
        .filter((c) => c.status === 'active')
        .reduce((acc, c) => acc + (Number(c.weight_pct) || 0), 0) * 100,
    ) / 100,
    [config],
  );

  // Checkbox = pause/resume switch.
  //   - First time the admin ticks a metric: append a fresh active entry.
  //   - Untick an active entry: flip its status to 'inactive'. Slabs +
  //     weight stay intact.
  //   - Re-tick an inactive entry: flip back to 'active' — everything
  //     the admin had configured is right where it was left.
  // This applies to every kind (system, task_question, target_metric);
  // scorecard_configs.status is the single source of truth.
  const toggleInclude = (def: CatalogEntry) => {
    setConfig((prev) => {
      const exists = prev.find((c) => c.metric_def_id === def.metric_def_id);
      if (exists) {
        return prev.map((c) =>
          c.metric_def_id === def.metric_def_id
            ? { ...c, status: c.status === 'active' ? 'inactive' : 'active' }
            : c,
        );
      }
      const nextOrder = prev.length;
      return [
        ...prev,
        {
          config_id: 0,
          metric_def_id: def.metric_def_id,
          code: def.code,
          kind: def.kind,
          name: def.name,
          weight_pct: 0,
          display_order: nextOrder,
          role_id: configRoleId,
          // Default scope: employee. System metrics are inherently
          // per-employee anyway; for task-question / target-metric the admin
          // can flip this via the segmented control in the row.
          attribution_scope: 'employee',
          // Engine defaults until the admin explicitly overrides (seed_score).
          config_params: null,
          // Default to raw scoring; UI exposes the toggle only for target_metric kind.
          score_basis: 'raw',
          status: 'active',
          slabs: [],
        },
      ];
    });
  };
  const updateScope = (metricDefId: number, scope: 'employee' | 'store') => {
    setConfig((prev) =>
      prev.map((c) =>
        c.metric_def_id === metricDefId ? { ...c, attribution_scope: scope } : c,
      ),
    );
  };
  // 'raw' → slab over the achieved value directly.
  // 'achievement_pct' → engine divides achievement by the period's target.
  // Inline delete for a target_metric directly from the Configure list.
  // Two-phase to keep the admin in control of cascade behaviour:
  //   1. First attempt: a plain DELETE. The server refuses with 409 +
  //      target_rows count if the metric has targets/achievements attached.
  //   2. On that 409, prompt to also wipe the referenced rows; if confirmed,
  //      retry with cascade=true. Otherwise abort.
  // After a successful delete we refresh the catalog so the row vanishes.
  const deleteTargetMetric = async (targetMetricId: number, name: string) => {
    if (deletingMetricId !== null) return;
    const firstOk = window.confirm(
      `Delete the custom metric "${name}"?\n\nThis removes it from every role's scorecard and from the project catalog.`,
    );
    if (!firstOk) return;
    setDeletingMetricId(targetMetricId);
    try {
      await targetMetricsAPI.delete(targetMetricId);
      await fetchConfig();
    } catch (err: any) {
      const code = err?.response?.data?.code;
      const targetRows = err?.response?.data?.target_rows;
      if (err?.response?.status === 409 && code === 'METRIC_IN_USE') {
        // Re-prompt with the exact count so admins know the blast radius.
        const cascadeOk = window.confirm(
          `"${name}" has ${targetRows} target / achievement row${targetRows === 1 ? '' : 's'} attached.\n\n` +
          `Click OK to delete the metric AND wipe those rows. Click Cancel to keep everything.`,
        );
        if (cascadeOk) {
          try {
            const res = await targetMetricsAPI.delete(targetMetricId, { cascade: true });
            const wiped = res?.data?.cascade_target_rows ?? 0;
            window.alert(`Deleted "${name}" and ${wiped} target / achievement row${wiped === 1 ? '' : 's'}.`);
            await fetchConfig();
          } catch (cascadeErr: any) {
            const msg = cascadeErr?.response?.data?.message || cascadeErr?.message || 'Cascade delete failed';
            window.alert(msg);
          }
        }
      } else {
        const msg = err?.response?.data?.message || err?.message || 'Failed to delete metric';
        window.alert(msg);
      }
    } finally {
      setDeletingMetricId(null);
    }
  };

  const updateScoreBasis = (metricDefId: number, basis: 'raw' | 'achievement_pct') => {
    setConfig((prev) =>
      prev.map((c) =>
        c.metric_def_id === metricDefId ? { ...c, score_basis: basis } : c,
      ),
    );
  };
  // Tweak a single key inside config_params (for editable seed_score knobs).
  // Empty/invalid input → removes the override so the engine falls back to
  // its default for that key only.
  const updateConfigParam = (metricDefId: number, key: string, raw: string) => {
    setConfig((prev) =>
      prev.map((c) => {
        if (c.metric_def_id !== metricDefId) return c;
        const next = { ...(c.config_params ?? {}) };
        const num = Number(raw);
        if (raw === '' || !Number.isFinite(num)) {
          delete next[key];
        } else {
          next[key] = num;
        }
        return { ...c, config_params: Object.keys(next).length > 0 ? next : null };
      }),
    );
  };
  // Replace the entire aon_buckets array on a metric's config_params. Used
  // by the Seed Score AON editor (add/remove/edit a bucket row writes the
  // whole array). Pass `null` to revert to engine defaults.
  const updateAonBuckets = (
    metricDefId: number,
    buckets: Array<{ name: string; low_months: number; high_months: number | null }> | null,
  ) => {
    setConfig((prev) =>
      prev.map((c) => {
        if (c.metric_def_id !== metricDefId) return c;
        const next = { ...(c.config_params ?? {}) };
        if (buckets === null) {
          delete next.aon_buckets;
        } else {
          next.aon_buckets = buckets;
        }
        return { ...c, config_params: Object.keys(next).length > 0 ? next : null };
      }),
    );
  };
  // What to do when an employee has NO value for this metric in the scored month:
  //   zero          — score it 0 (the default, and what happens with no setting)
  //   full          — award the top slab
  //   carry_forward — reuse the employee's most recent value from an earlier month
  // Mirrors missingStrategyOf() in apple-scorecard.service.ts, including the
  // fallback to the legacy `missing_as_full` boolean so configs saved before this
  // control existed keep reading correctly.
  type MissingStrategy = 'zero' | 'full' | 'carry_forward';
  const readMissingStrategy = (cp: Record<string, unknown> | null | undefined): MissingStrategy => {
    const p = cp ?? {};
    const raw = String(p.missing_strategy ?? '').trim();
    if (raw === 'zero' || raw === 'full' || raw === 'carry_forward') return raw;
    return p.missing_as_full === true ? 'full' : 'zero';
  };
  // Slab ladder vs. direct scoring. 'direct' means the metric's value IS its score,
  // so the slab editor is hidden and any existing bands are dropped on save — leaving
  // them would be dead config that the next admin would read as live.
  const setScoringMode = (metricDefId: number, value: 'slabs' | 'direct') => {
    setConfig((prev) =>
      prev.map((c) => {
        if (c.metric_def_id !== metricDefId) return c;
        const next = { ...(c.config_params ?? {}) };
        if (value === 'direct') next.scoring = 'direct';
        else delete next.scoring;
        return {
          ...c,
          config_params: Object.keys(next).length > 0 ? next : null,
          slabs: value === 'direct' ? [] : c.slabs,
        };
      }),
    );
  };
  const setMissingStrategy = (metricDefId: number, value: MissingStrategy) => {
    setConfig((prev) =>
      prev.map((c) => {
        if (c.metric_def_id !== metricDefId) return c;
        const next = { ...(c.config_params ?? {}) };
        // Retire the legacy boolean the moment an admin touches this control:
        // leaving both would give the same decision two sources of truth.
        delete next.missing_as_full;
        if (value === 'zero') delete next.missing_strategy;
        else next.missing_strategy = value;
        return { ...c, config_params: Object.keys(next).length > 0 ? next : null };
      }),
    );
  };
  const updateWeight = (metricDefId: number, value: string) => {
    const num = Number(value);
    setConfig((prev) =>
      prev.map((c) =>
        c.metric_def_id === metricDefId
          ? { ...c, weight_pct: Number.isFinite(num) ? num : 0 }
          : c,
      ),
    );
  };
  const addSlab = (metricDefId: number) => {
    setConfig((prev) =>
      prev.map((c) =>
        c.metric_def_id === metricDefId
          ? {
              ...c,
              slabs: [
                ...c.slabs,
                {
                  lower_bound: null,
                  upper_bound: null,
                  points: 0,
                  display_label: null,
                  display_order: c.slabs.length,
                },
              ],
            }
          : c,
      ),
    );
  };
  const removeSlab = (metricDefId: number, slabIdx: number) => {
    setConfig((prev) =>
      prev.map((c) =>
        c.metric_def_id === metricDefId
          ? { ...c, slabs: c.slabs.filter((_, i) => i !== slabIdx) }
          : c,
      ),
    );
  };
  const updateSlab = (metricDefId: number, slabIdx: number, patch: Partial<SlabRow>) => {
    setConfig((prev) =>
      prev.map((c) => {
        if (c.metric_def_id !== metricDefId) return c;
        const slabs = c.slabs.map((s, i) => (i === slabIdx ? { ...s, ...patch } : s));
        return { ...c, slabs };
      }),
    );
  };

  // True when the in-memory config diverges from the last loaded snapshot —
  // i.e. there's actually something to save. Save changes is gated on this
  // so admins can't accidentally fire an empty save (which previously
  // surfaced backend role_id-mismatch errors when no role was selected yet).
  const isConfigDirty = useMemo(
    () => JSON.stringify(config) !== configBaselineRef.current,
    [config],
  );

  // A save is only safe to fire when (a) the admin has picked a role that
  // belongs to the current project, AND (b) at least one edit is queued.
  // Either check missing in the past produced the 400 "role_id … does not
  // belong to project N" we saw on screen.
  const roleBelongsToProject = useMemo(
    () => !!configRoleId && roleOptions.some((r) => r.id === configRoleId),
    [configRoleId, roleOptions],
  );
  const canSave = roleBelongsToProject && isConfigDirty && !saving && !configLoading;

  const handleSave = async () => {
    if (!selectedProjectId) return;
    if (!canSave) return;
    // Learning Points cannot be scored without its monthly target; say so here
    // rather than after a round trip.
    const lmsWithoutTarget = config.some((c) => {
      if (c.code !== 'lms_points' || c.status !== 'active') return false;
      const target = Number(c.config_params?.monthly_target);
      return !(Number.isFinite(target) && target > 0);
    });
    if (lmsWithoutTarget) {
      setSaveMsg('Learning Points needs a monthly target — the points an employee is expected to earn in a month.');
      return;
    }
    setSaving(true);
    setSaveMsg(null);
    try {
      await scorecardAPI.saveConfig(selectedProjectId, {
        role_id: configRoleId,
        // Stamp this save with the selected month — replaces only that
        // month's version, leaving other months' configs untouched.
        effective_from: configEffMonth,
        metrics: config.map((c, idx) => {
          // System metrics are inherently per-employee — the Configure UI
          // hides their Employee/Store toggle, so force the saved scope to
          // match what the admin actually sees. Any 'store' value that
          // sneaked in from a previous build gets normalised on next save.
          // The same for a metric read from Sales & Targets (its row locks the
          // toggle to Employee); the API saves it per employee whatever is sent.
          const def = catalog.find((d) => d.metric_def_id === c.metric_def_id);
          const scope = def?.kind === 'system' || def?.has_sales_feed ? 'employee' : c.attribution_scope;
          return {
          metric_def_id: c.metric_def_id,
          weight_pct: c.weight_pct,
          display_order: idx,
          attribution_scope: scope,
          // Score basis (target_metric only). 'raw' = today's behaviour.
          score_basis: c.score_basis,
          // Per-metric tuning knobs (seed_score). null = engine defaults.
          config_params: c.config_params,
          // Persist pause/resume state. Inactive entries keep their
          // weight + slabs in the DB but the engine filters them out.
          status: c.status,
          slabs: c.slabs.map((s, j) => ({
            lower_bound: s.lower_bound,
            upper_bound: s.upper_bound,
            points: s.points,
            display_label: s.display_label ?? null,
            display_order: j,
          })),
          };
        }),
      });
      setSaveMsg('Saved');
      await Promise.all([fetchConfig(), fetchScore()]);
    } catch (err: any) {
      setSaveMsg(err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────
  if (!selectedProjectId) {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Employee Score</h1>
        <p className="mt-2 text-sm text-gray-500">Select a project to view its scorecard.</p>
      </div>
    );
  }

  return (
    // No outer padding here — DashboardLayout's <main> already provides
    // px-6 pt-4 pb-6. Adding p-6 again would double the margins and push
    // the heading down (visible mismatch vs other pages, e.g.
    // AppleScorecardPage which uses just `space-y-6`).
    <div className="space-y-4">
      {/* flex-wrap + min-w-0: the controls on the right are fixed-size, so on a narrower
          viewport they must WRAP to their own line rather than compress (which was
          breaking "6-Month Avg" across two lines and squashing the month picker). */}
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1 basis-[22rem]">
          <h1 className="text-2xl font-semibold text-gray-900">Employee Score</h1>
          <p className="mt-1 max-w-prose text-sm text-gray-500">
            Configurable per-project scorecard. View results under View; configure metrics
            and upload supporting data under Manage.
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center gap-3">
          {/* Monthly ↔ 6-Month Avg toggle. Monthly = the live single-month
              scorecard; 6-Month Avg = the last 6 snapshot totals + average,
              ending at the selected month. View-tab only, and only for the
              people active today: the Inactive tab shows one saved month. */}
          {tab === 'score' && staffView === 'active' && (
            <div className="inline-flex flex-shrink-0 rounded-md border border-gray-300 p-0.5 text-sm">
              <button
                type="button"
                onClick={() => setScoreView('monthly')}
                className={`whitespace-nowrap rounded px-3 py-1 font-medium transition ${
                  scoreView === 'monthly' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setScoreView('sixMonth')}
                className={`whitespace-nowrap rounded px-3 py-1 font-medium transition ${
                  scoreView === 'sixMonth' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Last 6 monthly scores + a 6-month average (÷6; months with no score count as 0), ending at the selected month"
              >
                6-Month Avg
              </button>
            </div>
          )}
          {/* Month applies to the View tabs AND every Manage metric sub-tab
              (all read the same `periodMonth`). Configure carries its own
              effective-month control, so it's excluded. The per-section Month
              pickers inside the Manage panels are suppressed (hideMonthPicker)
              in favour of this single top-level control. */}
          {(tab === 'score' || tab === 'quadrant' || (mode === 'manage' && !!activeMetricTab)) && (
            <div className="flex flex-shrink-0 items-center gap-2">
              <label className="whitespace-nowrap text-sm text-gray-600">Month</label>
              <input
                type="month"
                value={periodMonth.slice(0, 7)}
                // A deliberate pick — from here on, role changes never move it.
                onChange={(e) => { setPeriodMonth(`${e.target.value}-01`); setMonthSource('user'); }}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
              />
              {/* Two mutually exclusive hints, so the month is never unexplained:
                  • auto-resolved and NOT the last completed month → say why it moved;
                  • user-chosen and this role has data elsewhere → offer the jump
                    rather than moving them, since they picked this month on purpose. */}
              {(() => {
                if (!defaultMonths || mode !== 'view') return null;
                const roleTarget = (configRoleId && defaultMonths.byRole[configRoleId]?.month)
                  || defaultMonths.defaultMonth;
                if (monthSource === 'auto') {
                  return periodMonth !== defaultMonths.upto
                    ? <span className="whitespace-nowrap text-xs text-gray-500">latest scored month</span>
                    : null;
                }
                if (!roleTarget || roleTarget === periodMonth) return null;
                return (
                  <button
                    type="button"
                    onClick={() => setPeriodMonth(roleTarget)}
                    className="text-xs font-medium text-primary-600 underline-offset-2 hover:underline"
                  >
                    Show {roleTarget.slice(0, 7)}
                  </button>
                );
              })()}
            </div>
          )}
          {/* Export — on the View → Score tab (both monthly + 6-month) and on every
              Manage metric sub-tab with a table (see downloadOffered). Always the table on screen, as Excel: its
              columns and its rows in the order shown. Disabled while that table
              is loading or has no rows (see canDownload). */}
          {downloadOffered && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading || !canDownload}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Download the table as shown — same columns, rows and order — as Excel"
            >
              {downloading ? 'Preparing…' : 'Download'}
            </button>
          )}
        </div>
      </div>

      {/* A failed download is reported here, in one line, so the table stays on screen. */}
      {downloadError && (
        <p role="alert" className="text-sm text-rose-600">{downloadError}</p>
      )}

      {/* Months held until an admin says their sales are in. Above everything and
          on every tab, whatever month is selected: the page lands on the newest
          month that has scores, which is rarely the month still waiting. After a
          press the scores are read again, and an open Manage panel re-reads its
          figures. */}
      <SalesFeedWaitingBar
        waiting={salesFeedWaiting}
        onReleased={() => { setMetricDefsNonce((n) => n + 1); return fetchScore(); }}
      />

      {/* Single global control row: Role pills (the outer context) sit on
          the LEFT; the View / Manage mode toggle sits flush to the RIGHT.
          Role context applies to both modes — View tab shows whose scores
          you're looking at, Manage tab shows whose config you're editing —
          so the role row stays mounted regardless of mode. */}
      {rolesFetched && roleOptions.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Role</span>
          {roleOptions.map((r) => {
            const isActive = configRoleId === r.id;
            const isConfigured = rolesWithConfig.has(r.id);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setConfigRoleId(isActive ? null : r.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ring-1 ${
                  isActive
                    ? 'bg-primary-600 text-white ring-primary-600 shadow'
                    : isConfigured
                      ? 'bg-emerald-50 text-emerald-800 ring-emerald-200 hover:bg-emerald-100'
                      : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'
                }`}
                title={isConfigured ? 'Scorecard saved for this role' : 'No scorecard yet — click to set one up'}
              >
                {isConfigured && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={isActive ? 'text-white' : 'text-emerald-700'} aria-hidden>
                    <path d="M2 6.5l2.5 2.5L10 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
                <span>{r.name}</span>
              </button>
            );
          })}

          {/* View / Manage mode toggle — pushed to the far right. The auto
              margin lets it stay flush even when the role pills wrap to
              two lines on narrow viewports. */}
          <div className="ml-auto flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm font-medium">
            {([
              ['view',   'View'],
              ['manage', 'Manage'],
            ] as const).map(([m, label]) => (
              <button
                key={m}
                onClick={() => {
                  if (mode === m) return;
                  const defaultSub: TabKey =
                    m === 'view'
                      ? VIEW_TABS[0].key
                      : (visibleManageTabs[0]?.key ?? 'configure');
                  setTab(defaultSub);
                }}
                className={`rounded-md px-3 py-1 transition ${
                  mode === m
                    ? 'bg-white text-primary-700 shadow ring-1 ring-gray-200'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty-role fallback: when the project has zero roles defined, we
          still need a way to switch modes. Show a slim row with just the
          toggle. */}
      {rolesFetched && roleOptions.length === 0 && (
        <div className="flex justify-end">
          <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm font-medium">
            {([
              ['view',   'View'],
              ['manage', 'Manage'],
            ] as const).map(([m, label]) => (
              <button
                key={m}
                onClick={() => {
                  if (mode === m) return;
                  const defaultSub: TabKey =
                    m === 'view'
                      ? VIEW_TABS[0].key
                      : (visibleManageTabs[0]?.key ?? 'configure');
                  setTab(defaultSub);
                }}
                className={`rounded-md px-3 py-1 transition ${
                  mode === m
                    ? 'bg-white text-primary-700 shadow ring-1 ring-gray-200'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sub-tab strip — driven by the active mode. View has a single tab
          (Score) so we suppress its strip; Manage shows only the sub-tabs
          whose underlying data feed is set up for this project (others are
          hidden until enabled). */}
      {mode === 'manage' && (
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-6">
            {visibleManageTabs.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${
                  tab === key
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* SCORE TAB */}
      {tab === 'score' && (
        <>
          {/* Who the table lists: the people active today, or the people who have
              left with the score saved for them that month. Counts follow the role
              picked above and each tab's own filters, as the number under the
              table does. */}
          <StaffViewTabs
            view={staffView}
            onChange={setStaffView}
            activeCount={scoresReady ? filteredRows.length : null}
            inactiveCount={inactiveReady ? inactiveVisible.length : null}
          />
          {staffView === 'inactive' ? (
            <>
            {/* A month still waiting for an upload was saved with that parameter
                at zero, for the people who left too — and their saved score is
                not written again when the upload arrives. */}
            {inactiveReady && scoresReady && (
              <AwaitingUploadsNotice
                awaiting={awaitingUploads}
                month={periodMonth}
                roleId={configRoleId}
                roleNames={new Map(roleOptions.map((r) => [r.id, r.name]))}
              />
            )}
            <InactiveScoresPanel
              rows={inactiveVisible}
              month={periodMonth}
              loading={inactiveLoading || (!inactiveError && inactiveLoadedFor !== onScreenKey)}
              error={inactiveError}
              onRetry={fetchInactive}
              search={inactiveSearch}
              onSearch={setInactiveSearch}
              roleName={roleOptions.find((r) => r.id === configRoleId)?.name ?? null}
            />
            </>
          ) : (
          <>
          {/* Quadrant summary strip — distribution of the currently loaded
              scorecard rows into Stars / Achievers / Strivers / At Risk.
              Clicking a chip filters the table below. Unclassified is shown
              only if any employees fell into it. */}
          {rows.length > 0 && (
            <div className="space-y-2">
              {/* Quadrant distribution as stat cards — each card is a toggle
                  filter for the table below; click the active one again (or
                  "Clear filter") to reset. */}
              {/* All present quadrants — the four performance tiers, then the Bottom Box
                  (an overlapping subset of At Risk), plus Unclassified / Excluded (maternity
                  hold-out) when non-empty — as ONE row of stat-card toggle filters. (New
                  Joiner was retired — recent joiners now rank into a real tier.) */}
              <div className="flex flex-wrap gap-3">
                {[
                  ...QUADRANT_ORDER.map((q) => ({ key: q as Quadrant | typeof BOTTOM_BOX_KEY, count: quadrantCounts[q], style: QUADRANT_STYLE[q], title: QUADRANT_TITLE[q] ?? `Filter table to ${q}` })),
                  { key: BOTTOM_BOX_KEY, count: bottomBoxCount, style: BOTTOM_BOX_STYLE, title: BOTTOM_BOX_TITLE },
                  ...(['Unclassified', 'Excluded'] as Quadrant[])
                    .filter((q) => quadrantCounts[q] > 0)
                    .map((q) => ({ key: q as Quadrant | typeof BOTTOM_BOX_KEY, count: quadrantCounts[q], style: QUADRANT_STYLE[q], title: QUADRANT_TITLE[q] ?? `Filter table to ${q}` })),
                ].map(({ key, count, style: s, title }) => {
                  const Icon = s.icon;
                  const active = quadFilter === key;
                  return (
                    <button
                      key={key}
                      onClick={() => setQuadFilter(active ? null : key)}
                      title={active ? `Click to clear ${key} filter` : title}
                      className={`flex grow basis-40 items-center gap-3 rounded-xl border p-3 text-left transition ${
                        active
                          ? `${s.bg} ${s.ring} ring-2 shadow-sm`
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.iconBg} ${s.accent}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="min-w-0">
                        <span className={`block text-2xl font-bold leading-none ${s.accent}`}>{count}</span>
                        <span className="mt-1 block text-xs font-medium text-gray-500">{key}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              {quadFilter && (
                <button
                  onClick={() => setQuadFilter(null)}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800"
                >
                  Clear filter
                </button>
              )}
            </div>
          )}

          {/* Dimension filters — slice the report by Assistant Manager / Store
              Trade / City. Options come from the role-scoped rows; selections
              AND together and also narrow the quadrant chips above. */}
          {rows.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-gray-50/60 p-2">
              <FilterSelect icon={MapPin} value={filterCity} onChange={setFilterCity} allLabel="All Cities" options={cityOptions} />
              {/* Trade Head sits BEFORE Assistant Manager: parent then child, matching the
                  iPro -> AM -> Trade Head hierarchy and the Dashboard's filter order.
                  Selecting one narrows the AM list below it. */}
              {/* These two filters are HOP-based, not role-based: `th_code` is the listed
                  employee's manager's manager and `manager_code` is their direct manager.
                  That only equals "Trade Head"/"Assistant Manager" when the listed rows are
                  iPro promoters — pick the AM role and the rows ARE AMs, so their direct
                  manager is a Trade Head. Label them by the RELATIONSHIP so they are correct
                  for every role instead of silently mislabelling. */}
              <FilterSelect icon={Users} value={filterTH} onChange={(v) => { setFilterTH(v); setFilterAM(''); }} allLabel="All Skip-Level Managers" options={thOptions} />
              <FilterSelect icon={User} value={filterAM} onChange={setFilterAM} allLabel="All Reporting Managers" options={amOptions} />
              <FilterSelect icon={Store} value={filterTrade} onChange={setFilterTrade} allLabel="All Store Trades" options={tradeOptions} />
              <SearchInput value={filterText} onChange={setFilterText} />
              {(filterAM || filterTH || filterTrade || filterCity || filterText) && (
                <button
                  onClick={() => { setFilterAM(''); setFilterTH(''); setFilterTrade(''); setFilterCity(''); setFilterText(''); }}
                  className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 transition hover:bg-gray-200/70 hover:text-gray-700"
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </button>
              )}
              <span className="ml-auto text-xs font-medium text-gray-500">{filteredRows.length} employees</span>
            </div>
          )}

        {/* The month is held for an upload: say so above figures that would
            otherwise read as a final month in which everybody scored low. Only
            once the rows on screen are the ones this was loaded with, and only
            over the monthly table: the 6-month view shows other months too. */}
        {scoreView === 'monthly' && !scoreLoading && !scoreError && rowsLoadedFor === loadKey(selectedProjectId, periodMonth) && (
          <AwaitingUploadsNotice
            awaiting={awaitingUploads}
            month={periodMonth}
            roleId={configRoleId}
            roleNames={new Map(roleOptions.map((r) => [r.id, r.name]))}
          />
        )}

        {/* Per-metric compute failures. The engine isolates these — every other
            metric still scored — so this is a warning above a valid table, not an
            error replacing it. Surfacing it matters because the alternative is a
            metric that silently disappears from every row with no explanation. */}
        {!scoreLoading && !scoreError && failedMetrics.length > 0 && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="font-medium">
              {failedMetrics.length === 1 ? 'A metric' : `${failedMetrics.length} metrics`} couldn&apos;t be computed for this month
            </div>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-700">
              {failedMetrics.map((f) => (
                <li key={f.code}>
                  <span className="font-medium">
                    {config.find((c) => c.code === f.code)?.name ?? f.code}
                  </span>
                  {f.error ? ` — ${f.error}` : null}
                </li>
              ))}
            </ul>
            <div className="mt-1.5 text-amber-600">
              Those columns are blank below; every other metric scored normally. This is a data-source issue rather than a configuration one, so it often clears on a reload.
            </div>
          </div>
        )}
        {!scoreLoading && !scoreError && namesFellBack && (
          <p className="mb-3 text-xs text-gray-500">
            Some names couldn&apos;t be loaded from 1Office, so their employee codes are shown. Reload in a minute.
          </p>
        )}

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
          {scoreLoading || (scoreView === 'sixMonth' && sixMonthLoading) ? (
            <div className="p-8 text-sm text-gray-500"><InlineLoader label="Loading…" className="flex" /></div>
          ) : scoreError ? (
            <div className="p-8 text-sm text-rose-600">{scoreError}</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-sm text-gray-500">
              No scorecard data for this month. Either no metrics are configured (see the Configure tab) or there are no employees with values yet.
            </div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <SortHeader
                      label="Employee Name"
                      align="left"
                      active={viewSortKey === '__emp_name__'}
                      dir={viewSortDir}
                      pinClassName="sticky top-0 left-0 z-30"
                      onClick={() => handleViewSort('__emp_name__')}
                    />
                    <SortHeader
                      label="Employee Code"
                      align="left"
                      active={viewSortKey === '__emp_code__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__emp_code__')}
                    />
                    <SortHeader
                      label="Store Name"
                      align="left"
                      active={viewSortKey === '__store_name__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__store_name__')}
                    />
                    <SortHeader
                      label="Store Code"
                      align="left"
                      active={viewSortKey === '__store_code__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__store_code__')}
                    />
                    <SortHeader
                      label="Store Address"
                      align="left"
                      active={viewSortKey === '__store_address__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__store_address__')}
                    />
                    {scoreView === 'monthly' ? (
                      <>
                        {metricColumns.map((m) => (
                          <SortHeader
                            key={m.code}
                            label={m.name}
                            active={viewSortKey === m.code}
                            dir={viewSortDir}
                            sticky
                            onClick={() => handleViewSort(m.code)}
                          />
                        ))}
                        <SortHeader
                          label="Total"
                          active={viewSortKey === '__total__'}
                          dir={viewSortDir}
                          sticky
                          onClick={() => handleViewSort('__total__')}
                        />
                      </>
                    ) : (
                      <>
                        {(sixMonth?.months ?? []).map((m) => (
                          <SortHeader
                            key={m}
                            label={fmtMonthShort(m)}
                            active={viewSortKey === `m:${m}`}
                            dir={viewSortDir}
                            sticky
                            onClick={() => handleViewSort(`m:${m}`)}
                          />
                        ))}
                        <SortHeader
                          label="Average"
                          active={viewSortKey === '__avg__'}
                          dir={viewSortDir}
                          sticky
                          onClick={() => handleViewSort('__avg__')}
                        />
                      </>
                    )}
                    <SortHeader
                      label="Rank"
                      active={viewSortKey === '__rank__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__rank__')}
                    />
                    <SortHeader
                      label="AON"
                      active={viewSortKey === '__aon__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__aon__')}
                    />
                    <SortHeader
                      label="Quadrant"
                      align="left"
                      active={viewSortKey === '__quadrant__'}
                      dir={viewSortDir}
                      sticky
                      onClick={() => handleViewSort('__quadrant__')}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedRows.map((r) => (
                    <tr key={r.employee_code}>
                      <td className="px-3 py-2 font-medium text-gray-900 sticky left-0 z-10 bg-white">{r.employee_name}</td>
                      <td className="px-3 py-2 text-gray-700">{r.employee_code}</td>
                      <td className="px-3 py-2 text-gray-700">{r.store_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-700">{r.store_code || '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{r.store_address || '—'}</td>
                      {scoreView === 'monthly' ? (
                        <>
                          {metricColumns.map((col) => {
                            const m = r.metrics.find((x) => x.code === col.code);
                            // Per-metric cell shows the WEIGHTED contribution
                            // (points × weight / 100) — the same number as the
                            // Manage → Per-employee scores "Weighted" column. This
                            // makes the metric columns sum across to the Total.
                            // The raw value / unweighted points breakdown lives on
                            // the per-metric Manage tab. A row with no entry for
                            // this metric (outside the role roster) shows '—'.
                            return (
                              <td key={col.code} className="px-3 py-2 text-right text-gray-900">
                                {m ? m.contribution.toFixed(2) : '—'}
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">
                            {/* Pre-join month: employee had not joined yet, so the
                                backend sent a blank row — show "—" instead of 0.00. */}
                            {r.preJoin ? '—' : r.total.toFixed(2)}
                          </td>
                        </>
                      ) : (
                        <>
                          {(sixMonth?.months ?? []).map((m) => {
                            // Snapshot total for this month, or '—' when that
                            // month has no snapshot (excluded from the average).
                            const v = r.six?.totals[m];
                            return (
                              <td key={m} className="px-3 py-2 text-right text-gray-900">
                                {v === undefined ? '—' : v.toFixed(2)}
                              </td>
                            );
                          })}
                          <td className="px-3 py-2 text-right font-semibold text-gray-900">
                            {r.six?.avg != null ? r.six.avg.toFixed(2) : '—'}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap tabular-nums">
                        {(() => {
                          const rk = rankOf(r);
                          return rk ? (
                            <>
                              <span className="font-medium text-gray-900">{rk.rank}</span>
                              <span className="text-gray-400"> / {rk.total}</span>
                            </>
                          ) : '—';
                        })()}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap tabular-nums">
                        {aonLabel(r.date_of_joining)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${QUADRANT_STYLE[r.quadrant].bg} ${QUADRANT_STYLE[r.quadrant].text} ${QUADRANT_STYLE[r.quadrant].ring}`}
                          >
                            {r.quadrant}
                          </span>
                          {r.is_bottom_box && (
                            <span
                              className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${BOTTOM_BOX_STYLE.bg} ${BOTTOM_BOX_STYLE.text} ${BOTTOM_BOX_STYLE.ring}`}
                              title={BOTTOM_BOX_TITLE}
                            >
                              <ShieldAlert className="h-3 w-3" /> Bottom Box
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {quadFilter && filteredRows.length === 0 && (
                <div className="p-6 text-sm text-gray-500">No employees in this quadrant for the selected month.</div>
              )}
            </div>
          )}
        </div>
          </>
          )}
        </>
      )}

      {/* CONFIGURE TAB */}
      {tab === 'configure' && (
        <div className="space-y-4">
          {/* The role picker lives in the pill row above (visible across
              every Manage sub-tab). When the project has zero roles defined
              we still surface a guidance card here so admins know to create
              roles first. */}
          {!configRoleId && rolesFetched && roleOptions.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-medium">This project has no roles defined.</div>
              <div className="mt-1 text-xs">
                Create roles under <strong>Settings → Access Management</strong> first; then return here to
                configure each role's scorecard.
              </div>
            </div>
          )}

          {/* Everything below this is gated on a role being chosen. */}
          {configRoleId && (
          <>
          <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
            Pick which metrics count, give each a weight, and define slabs (raw value → points). Weights must
            sum to <strong>100%</strong>. Slab points are uncapped — bonus tiers above 100 are allowed and let the
            total exceed 100 when a strong performer hits them.
          </div>

          {/* Effective-month selector. Each config is versioned by the month
              it applies FROM — past months keep their own config, so changing
              this month's metrics never rewrites earlier months' scores.
              Saving writes/replaces only the selected month's version.

              The timeline below makes the month→config mapping explicit so
              admins can see exactly which version scores which months. */}
          {(() => {
            // Ascending list of saved boundaries; string sort works on
            // YYYY-MM-DD. Each version covers [its start, the month before the
            // next start]; the latest one runs "onwards".
            const versionsAsc = [...configVersions].sort();
            const isExisting = configVersions.includes(configEffMonth);
            // The version currently in effect at the picked month — what a NEW
            // version is pre-filled from, and what still covers earlier months.
            const effectiveBefore = versionsAsc.filter((v) => v <= configEffMonth).pop();
            return (
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <label className="font-medium text-gray-700">Editing config that applies from</label>
                  <input
                    type="month"
                    value={configEffMonth.slice(0, 7)}
                    onChange={(e) => e.target.value && setConfigEffMonth(`${e.target.value}-01`)}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                  <span className="text-xs text-gray-500">
                    onwards — earlier months keep their own config.
                  </span>
                </div>

                {/* New-vs-existing indicator for the picked month. */}
                <div className="text-xs">
                  {isExisting ? (
                    <span className="text-gray-600">
                      Editing the{' '}
                      <span className="font-semibold text-gray-800">{fmtVersionMonth(configEffMonth)}</span>{' '}
                      version — saving updates only this version.
                    </span>
                  ) : (
                    <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">
                      New version starting{' '}
                      <span className="font-semibold">
                        {new Date(`${configEffMonth}T00:00:00Z`).toLocaleDateString('en-US', {
                          month: 'short',
                          year: 'numeric',
                          timeZone: 'UTC',
                        })}
                      </span>
                      {effectiveBefore && (
                        <> — pre-filled from the <span className="font-semibold">{fmtVersionMonth(effectiveBefore)}</span> config; earlier months stay on it.</>
                      )}{' '}
                      Saving creates it without touching earlier months.
                    </span>
                  )}
                </div>

                {/* Timeline: which saved version scores which months. */}
                {versionsAsc.length > 0 && (
                  <div className="text-xs text-gray-600">
                    <div className="mb-1 font-medium text-gray-500">Which config scores which months</div>
                    <ul className="space-y-0.5">
                      {versionsAsc.map((v, i) => {
                        const next = versionsAsc[i + 1];
                        const range = next
                          ? v === SENTINEL_VERSION
                            ? `All months through ${monthBeforeLabel(next)}`
                            : `${fmtVersionMonth(v)} – ${monthBeforeLabel(next)}`
                          : v === SENTINEL_VERSION
                            ? 'All months'
                            : `${fmtVersionMonth(v)} onwards`;
                        const isCurrent = v === configEffMonth;
                        return (
                          <li key={v} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setConfigEffMonth(v)}
                              className={`rounded px-1.5 py-0.5 ${
                                isCurrent
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-white text-blue-600 ring-1 ring-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {fmtVersionMonth(v)}
                            </button>
                            <span className="text-gray-500">→ {range}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              Total weight:{' '}
              <span
                className={`font-semibold ${
                  Math.abs(weightTotal - 100) < 0.01
                    ? 'text-emerald-600'
                    : weightTotal === 0
                      ? 'text-gray-500'
                      : 'text-amber-600'
                }`}
              >
                {weightTotal}%
              </span>{' '}
              {config.length > 0 && Math.abs(weightTotal - 100) >= 0.01 && (
                <span className="text-xs text-amber-600">(needs to be exactly 100% to save)</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {/* Direct New-metric CTA — lands straight on the editor.
                  Editing existing custom metrics is reached via the
                  pencil icon on each Custom row below. fetchConfig
                  re-runs on close so any new metric shows up. */}
              <button
                onClick={openAddMetric}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                title="Create a new custom metric"
              >
                + Add Metric
              </button>
              {saveMsg && (
                <span className={saveMsg === 'Saved' ? 'text-sm text-emerald-600' : 'text-sm text-rose-600'}>
                  {saveMsg}
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={!canSave}
                title={
                  !roleBelongsToProject
                    ? 'Pick a role first'
                    : !isConfigDirty
                      ? 'No changes to save'
                      : undefined
                }
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>

          {configError && <div className="text-sm text-rose-600">{configError}</div>}
          {configLoading && <InlineLoader label="Loading…" className="flex" />}

          {/* Catalog list grouped by kind. Each entry: include checkbox + weight
              + slabs editor. */}
          {(['system', 'task_question', 'target_metric'] as const).map((kind) => {
            const items = catalog
              .filter((c) => c.kind === kind)
              // Hide retired system metrics (am_feedback / seed_score) from the
              // picker unless they're already included in this role's config.
              .filter((c) => !RETIRED_SYSTEM_CODES.has(c.code) || includedById.has(c.metric_def_id));
            if (items.length === 0) return null;
            // Per-kind colour accent — quickly distinguishes System metrics
            // from Task-Question / Target-Metric sources at a glance.
            const kindAccent = {
              system: { bar: 'bg-sky-500', tag: 'bg-sky-50 text-sky-700 ring-sky-200' },
              task_question: { bar: 'bg-violet-500', tag: 'bg-violet-50 text-violet-700 ring-violet-200' },
              target_metric: { bar: 'bg-emerald-500', tag: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
            }[kind];
            // Only count actively-included metrics for the "(N of M included)"
            // header counter — paused entries should not inflate the badge.
            const includedInKind = items.filter((i) => {
              const cfg = includedById.get(i.metric_def_id);
              return !!cfg && cfg.status === 'active';
            }).length;
            return (
              <div key={kind} className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/60 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${kindAccent.bar}`} aria-hidden />
                    <span className="text-sm font-semibold text-gray-800">{KIND_LABEL[kind]}</span>
                    <span className="text-xs text-gray-500">
                      ({includedInKind} of {items.length} included)
                    </span>
                  </div>
                  {/* Top-row "+ Manage Metrics" button (next to Save changes)
                      is the single entry point for add / edit / delete —
                      no duplicate CTA here. */}
                </div>
                <div className="divide-y divide-gray-100">
                  {items.map((def) => {
                    const cfg = includedById.get(def.metric_def_id);
                    // "included" = checkbox is ticked = entry exists AND
                    // is currently active. Inactive entries live in
                    // state (so unchecking preserves their slabs) but
                    // render as visually unchecked + paused.
                    const included = !!cfg && cfg.status === 'active';
                    return (
                      <div
                        key={def.metric_def_id}
                        className={`px-4 py-3 transition ${included ? 'bg-white' : 'bg-gray-50/40'}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <label className="flex items-center gap-3 cursor-pointer flex-1 min-w-0">
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-primary-600"
                              checked={included}
                              onChange={() => toggleInclude(def)}
                            />
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-medium text-gray-900">{def.name}</span>
                                {/* Unit + direction live next to the name now —
                                    the surrounding container header already
                                    says "System" / "Custom" / "Task Question",
                                    so the inline kind chip duplicated that
                                    grouping. Keeping it lean: just the bits
                                    that describe what the metric measures. */}
                                {/* Normalized system metrics are scored on a
                                    0–100% scale (count metrics inverted), so show
                                    "%" + "higher is better" regardless of the
                                    stored unit/direction. */}
                                {NORMALIZED_METRIC_HINT[def.code] !== undefined ? (
                                  <>
                                    <span className="text-xs text-gray-500">· %</span>
                                    <span className="text-xs text-emerald-600">· ↑ higher is better</span>
                                  </>
                                ) : (
                                  <>
                                    {def.unit && (
                                      <span className="text-xs text-gray-500">· {def.unit}</span>
                                    )}
                                    <span className={`text-xs ${def.higher_is_better ? 'text-emerald-600' : 'text-amber-600'}`}>
                                      · {def.higher_is_better ? '↑ higher is better' : '↓ lower is better'}
                                    </span>
                                  </>
                                )}
                                {/* Delete button used to live here next to the
                                    metric name. It was moved out of the label
                                    to the row's extreme right (rendered below
                                    after the included controls) and bumped to
                                    default icon size — easier to find, harder
                                    to fat-finger by mistake. */}
                              </div>
                              {/* Unit + direction moved up next to the metric
                                  name (see comment above). The internal
                                  machine code (def.code) was also dropped —
                                  it's a DB identifier the end user shouldn't
                                  have to parse. */}
                            </div>
                          </label>
                          {included && (
                            <div className="flex items-center gap-3 flex-shrink-0">
                              {/* Employee / Store scope segmented control.
                                  Hidden for system kinds — every system
                                  metric is computed per-employee by
                                  definition (Grooming = per individual,
                                  Leave/Absence = the employee's leave days,
                                  On-Time Arrival = the employee's punch
                                  record, Random Attendance = per slot per
                                  employee, Training Participation = per
                                  employee's attendance). Surfacing the
                                  toggle there would imply a configurability
                                  that doesn't exist on the data side.
                                  Shown only for task_question and
                                  target_metric (Custom) kinds, where the
                                  admin really does have a choice. */}
                              {def.kind !== 'system' && (() => {
                                // employeeOnly stays as a hard lock for a
                                // handful of non-system codes that are
                                // inherently per-individual too (e.g.
                                // seed_score, am_feedback).
                                // A metric read from Sales & Targets is one too:
                                // its figures are stored against each person, so
                                // a store average would mix a manager's team
                                // total into the promoters' own percentages.
                                const employeeOnly = EMPLOYEE_ONLY_METRIC_CODES.has(def.code) || !!def.has_sales_feed;
                                // Shown as it will be saved: a row left on Store
                                // from before the metric was moved to Sales &
                                // Targets is written per employee on the next save.
                                const scope = def.has_sales_feed ? 'employee' : cfg!.attribution_scope;
                                return (
                                  <div
                                    className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5 text-xs"
                                    title={employeeOnly
                                      ? 'This metric is always evaluated per-employee. Store-level attribution does not apply.'
                                      : 'Per-employee: each employee\'s own value. Per-store: store-level value applied to every deployed employee.'}
                                  >
                                    {(['employee', 'store'] as const).map((s) => {
                                      const active = scope === s;
                                      const disabled = employeeOnly && s === 'store';
                                      return (
                                        <button
                                          key={s}
                                          type="button"
                                          disabled={disabled}
                                          onClick={() => !disabled && updateScope(def.metric_def_id, s)}
                                          className={`rounded px-2 py-0.5 capitalize transition ${
                                            active
                                              ? 'bg-primary-100 text-primary-700'
                                              : disabled
                                                ? 'text-gray-300 cursor-not-allowed'
                                                : 'text-gray-500 hover:text-gray-800'
                                          }`}
                                        >
                                          {s}
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              })()}

                              {/* Score by: only meaningful for target_metric.
                                  'raw' uses the achieved value as-is; 'pct'
                                  divides achievement by the period's target.
                                  Pairs perfectly with hybrid metrics whose
                                  achievement is auto-derived from task answers.
                                  Hidden for a metric read from Sales & Targets:
                                  its formula already divides sold by target, and
                                  the switch would change nothing. */}
                              {def.kind === 'target_metric' && !def.has_sales_feed && (
                                <div
                                  className="inline-flex items-center rounded-md border border-gray-300 bg-white p-0.5 text-xs"
                                  title="Raw: score the achieved value via the slabs.  Achievement %: divide achievement by the period's target before the slab lookup."
                                >
                                  {([
                                    ['raw', 'Raw'],
                                    ['achievement_pct', '%'],
                                  ] as const).map(([val, label]) => {
                                    const active = cfg!.score_basis === val;
                                    return (
                                      <button
                                        key={val}
                                        type="button"
                                        onClick={() => updateScoreBasis(def.metric_def_id, val)}
                                        className={`rounded px-2 py-0.5 transition ${
                                          active ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:text-gray-800'
                                        }`}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}

                              <label className="text-xs text-gray-600">Weight %</label>
                              <input
                                type="number"
                                step="0.01"
                                min={0}
                                max={100}
                                value={cfg!.weight_pct}
                                onChange={(e) => updateWeight(def.metric_def_id, e.target.value)}
                                className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                              />

                              {/* Learning Points has no natural maximum, so the admin
                                  sets its "100%": the points an employee should earn
                                  in a month. Required — the API refuses a save
                                  without it. */}
                              {def.kind === 'system' && def.code === 'lms_points' && (() => {
                                const target = Number((cfg!.config_params as Record<string, unknown> | null)?.monthly_target);
                                const missing = !(Number.isFinite(target) && target > 0);
                                return (
                                  <>
                                    <label className="ml-2 text-xs text-gray-600" htmlFor={`lms-target-${def.metric_def_id}`}>
                                      Monthly target (pts)
                                    </label>
                                    <input
                                      id={`lms-target-${def.metric_def_id}`}
                                      type="number"
                                      step="1"
                                      min={1}
                                      placeholder="e.g. 200"
                                      value={missing ? '' : target}
                                      onChange={(e) => updateConfigParam(def.metric_def_id, 'monthly_target', e.target.value)}
                                      title="The learning points an employee is expected to earn in a month. Earning this many or more scores 100%."
                                      className={`w-24 rounded border px-2 py-1 text-right text-sm focus:outline-none focus:ring-1 ${
                                        missing
                                          ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                                          : 'border-gray-300 focus:border-primary-500 focus:ring-primary-200'
                                      }`}
                                    />
                                  </>
                                );
                              })()}

                              {/* What happens when an employee has no value for this
                                  metric in the scored month. Replaces the older
                                  "No value = full score" checkbox, which is still
                                  read for configs saved before this control existed.
                                  "Use last available month" is offered only for
                                  Custom metrics — a system metric's loader pre-seeds
                                  every employee, so a blank there means "no adverse
                                  events", not "no data", and carrying it forward
                                  would re-apply a stale penalty. The API rejects the
                                  combination too, so a stale client can't save it. */}
                              {/* Scoring mode. "Direct" suits a metric already expressed
                                  on the 0-100 points scale — Team Average Score most of
                                  all, where a slab ladder would need 101 one-point bands
                                  to say "68.34 scores 68". */}
                              <label className="ml-2 text-xs text-gray-600" htmlFor={`scoring-${def.metric_def_id}`}>
                                Score by
                              </label>
                              <select
                                id={`scoring-${def.metric_def_id}`}
                                value={(cfg!.config_params as Record<string, unknown> | null)?.scoring === 'direct' ? 'direct' : 'slabs'}
                                onChange={(e) => setScoringMode(def.metric_def_id, e.target.value as 'slabs' | 'direct')}
                                title="Slabs: match the value against the bands below. Direct: the value itself is the score, and no bands are used."
                                className="rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                              >
                                <option value="slabs">slabs</option>
                                <option value="direct">the value itself</option>
                              </select>

                              <label className="ml-2 text-xs text-gray-600" htmlFor={`missing-${def.metric_def_id}`}>
                                No value
                              </label>
                              <select
                                id={`missing-${def.metric_def_id}`}
                                value={readMissingStrategy(cfg!.config_params as Record<string, unknown> | null)}
                                onChange={(e) => setMissingStrategy(def.metric_def_id, e.target.value as MissingStrategy)}
                                title="What to score when an employee has no value for this metric in the scored month."
                                className="rounded border border-gray-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-200"
                              >
                                <option value="zero">= zero</option>
                                <option value="full">= full score</option>
                                {(def.kind === 'target_metric' || def.kind === 'task_question') && (
                                  <option value="carry_forward">= last available month</option>
                                )}
                              </select>
                            </div>
                          )}

                          {/* Edit + Delete pair — only for Custom metrics
                              (admin-owned). The Edit pencil opens the
                              Manage Metrics modal pre-positioned to this
                              metric so admins can change basics, sub-
                              metrics, or the formula without bouncing
                              through the list view. ml-auto pushes the
                              pair to the row's far edge. */}
                          {def.kind === 'target_metric' && def.target_metric_id != null && (
                            <div className="ml-auto flex flex-shrink-0 items-center gap-1">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openEditMetric(def.target_metric_id as number, configEffMonth);
                                }}
                                className="rounded-md p-2 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                                title={`Edit the "${def.name}" metric — change basics, sub-metrics, or formula`}
                                aria-label={`Edit ${def.name}`}
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                                  <path d="M12 20h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                  <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deleteTargetMetric(def.target_metric_id as number, def.name);
                                }}
                                disabled={deletingMetricId === def.target_metric_id}
                                className="rounded-md p-2 text-rose-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
                                title={`Delete the "${def.name}" metric from the project catalog`}
                                aria-label={`Delete ${def.name}`}
                              >
                                {deletingMetricId === def.target_metric_id ? (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="animate-spin" aria-hidden>
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.25" />
                                    <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                  </svg>
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                                    <path d="M4 7h16M9 7V4h6v3M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Formula card — shows how this metric's raw value
                            is computed. Custom metrics show their own
                            stored formula (e.g. "achievement / target *
                            100"); system + task_question metrics use the
                            static per-kind / per-code description. */}
                        {included && (() => {
                          // Custom metric — surface the actual formula
                          // the admin set in Manage Metrics.
                          if (def.kind === 'target_metric') {
                            const customFormula = def.target_metric_formula;
                            return (
                              <details className="mt-3 rounded-md border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700">
                                <summary className="cursor-pointer font-medium text-gray-700">
                                  How is this calculated?
                                </summary>
                                {customFormula ? (
                                  <>
                                    <p className="mt-2 text-gray-700">
                                      The metric's value per employee is computed by evaluating its
                                      stored formula over its sub-metrics for the period.
                                    </p>
                                    <p className="mt-1 font-mono text-[11px] text-gray-700">
                                      <code className="rounded bg-white px-1.5 py-0.5 border border-gray-200">{customFormula}</code>
                                    </p>
                                    <p className="mt-1 text-[11px] text-gray-500">
                                      Edit the formula or its sub-metrics via <strong>Manage Metrics</strong>.
                                      Each sub-metric supplies one numeric input, sourced from a
                                      manual upload, auto-aggregated from a task question, or
                                      read from Sales &amp; Targets.
                                    </p>
                                  </>
                                ) : (
                                  <p className="mt-2 text-gray-700">
                                    No formula has been set for this metric yet. Open{' '}
                                    <strong>Manage Metrics</strong> → this metric → add sub-metrics
                                    and a formula (or use the simple value-source picker on the
                                    Manage tab) before the engine can score it.
                                  </p>
                                )}
                              </details>
                            );
                          }
                          // System / task_question — static per-code or
                          // per-kind explanation from METRIC_FORMULAS.
                          const formula = METRIC_FORMULAS[def.code] ?? METRIC_FORMULAS[def.kind];
                          if (!formula) return null;
                          return (
                            <details className="mt-3 rounded-md border border-gray-100 bg-gray-50/60 p-3 text-xs text-gray-700">
                              <summary className="cursor-pointer font-medium text-gray-700">
                                How is this calculated?
                              </summary>
                              <p className="mt-2 text-gray-700">{formula.summary}</p>
                              <p className="mt-1 text-[11px] text-gray-500">{formula.details}</p>
                            </details>
                          );
                        })()}

                        {/* Seed Score — editable cohort math params. Each
                            field is optional; leaving it blank falls back to
                            the engine default for that key only. */}
                        {included && def.code === 'seed_score' && (() => {
                          const p = cfg!.config_params ?? {};
                          // Scalar tunables — single numeric input each.
                          const fields: { key: string; label: string; placeholder: string; step?: string }[] = [
                            { key: 'honeymoon_months',       label: 'Honeymoon window (months)',           placeholder: '3' },
                            { key: 'honeymoon_points',       label: 'Honeymoon points',                    placeholder: '20' },
                            { key: 'cohort_too_small_points', label: 'Cohort size ≤ 1 → points',           placeholder: '20' },
                            { key: 'sigma_multiplier',       label: 'σ multiplier (band half-width)',     placeholder: '1', step: '0.01' },
                            { key: 'tier_top_points',        label: 'Score > median + σ → points',         placeholder: '20' },
                            { key: 'tier_high_points',       label: 'median ≤ score ≤ median + σ → points', placeholder: '15' },
                            { key: 'tier_low_points',        label: 'median − σ ≤ score < median → points', placeholder: '10' },
                            { key: 'tier_bottom_points',     label: 'Score < median − σ → points',          placeholder: '0' },
                          ];

                          // AON buckets — start from the configured array, or
                          // fall back to the engine defaults so the admin can
                          // see them and edit if they want. Edits write the
                          // whole array back via updateAonBuckets.
                          const DEFAULT_BUCKETS = [
                            { name: '3-6m',  low_months: 3,  high_months: 6   },
                            { name: '6-9m',  low_months: 6,  high_months: 9   },
                            { name: '9-12m', low_months: 9,  high_months: 12  },
                            { name: '1-2y',  low_months: 12, high_months: 24  },
                            { name: '2y+',   low_months: 24, high_months: null },
                          ];
                          const customised = Array.isArray(p.aon_buckets) && p.aon_buckets.length > 0;
                          const currentBuckets: Array<{ name: string; low_months: number; high_months: number | null }> =
                            customised ? p.aon_buckets : DEFAULT_BUCKETS;

                          const updateBucket = (idx: number, patch: Partial<{ name: string; low_months: number; high_months: number | null }>) => {
                            const next = currentBuckets.map((b, i) => (i === idx ? { ...b, ...patch } : b));
                            updateAonBuckets(def.metric_def_id, next);
                          };
                          const addBucket = () => {
                            const last = currentBuckets[currentBuckets.length - 1];
                            // Insert a new bucket between the last finite one
                            // and the unbounded tail (if any).
                            const next = [...currentBuckets];
                            if (last && last.high_months === null) {
                              const tail = next.pop()!;
                              const newLow = tail.low_months;
                              const newHigh = newLow + 6;
                              next.push({ name: `${newLow}-${newHigh}m`, low_months: newLow, high_months: newHigh });
                              next.push({ ...tail, low_months: newHigh });
                            } else if (last) {
                              const newLow = last.high_months ?? last.low_months;
                              next.push({ name: `${newLow}m+`, low_months: newLow, high_months: null });
                            } else {
                              next.push({ name: '3-6m', low_months: 3, high_months: 6 });
                            }
                            updateAonBuckets(def.metric_def_id, next);
                          };
                          const removeBucket = (idx: number) => {
                            if (currentBuckets.length <= 1) return;
                            const next = currentBuckets.filter((_, i) => i !== idx);
                            updateAonBuckets(def.metric_def_id, next);
                          };

                          return (
                            <div className="mt-3 space-y-3 rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
                              <div>
                                <div className="text-xs font-semibold text-indigo-900">Cohort math — tunable parameters</div>
                                <p className="mt-0.5 text-[11px] text-indigo-800">
                                  Leave a field blank to use the engine default for that key only. σ multiplier
                                  scales the cohort standard deviation when bucketing employees into tiers (default 1
                                  = one σ). AON buckets below let you redraw the cohort boundaries.
                                </p>
                              </div>

                              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {fields.map((f) => (
                                  <label key={f.key} className="flex items-center justify-between gap-2 text-xs text-gray-700">
                                    <span className="flex-1">{f.label}</span>
                                    <input
                                      type="number"
                                      step={f.step ?? '1'}
                                      placeholder={f.placeholder}
                                      value={p[f.key] ?? ''}
                                      onChange={(e) => updateConfigParam(def.metric_def_id, f.key, e.target.value)}
                                      className="w-24 rounded border border-indigo-200 bg-white px-2 py-1 text-right focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-200"
                                    />
                                  </label>
                                ))}
                              </div>

                              {/* AON bucket editor */}
                              <div className="rounded-md border border-indigo-100 bg-white/60 p-2">
                                <div className="flex items-center justify-between">
                                  <div className="text-xs font-medium text-indigo-900">
                                    AON cohort buckets
                                    {!customised && (
                                      <span className="ml-2 text-[10px] font-normal text-indigo-600">(engine defaults — edit any field to override)</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {customised && (
                                      <button
                                        type="button"
                                        onClick={() => updateAonBuckets(def.metric_def_id, null)}
                                        className="text-[11px] font-medium text-gray-600 hover:text-gray-900"
                                      >
                                        Reset to defaults
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={addBucket}
                                      className="text-[11px] font-medium text-primary-700 hover:text-primary-900"
                                    >
                                      + Add bucket
                                    </button>
                                  </div>
                                </div>
                                <table className="mt-2 min-w-full text-xs">
                                  <thead className="text-gray-500">
                                    <tr>
                                      <th className="px-2 py-1 text-left">Name</th>
                                      <th className="px-2 py-1 text-right">From (months ≥)</th>
                                      <th className="px-2 py-1 text-right">To (months &lt;)</th>
                                      <th className="text-center px-3 py-2">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {currentBuckets.map((b, idx) => {
                                      const isLast = idx === currentBuckets.length - 1;
                                      return (
                                        <tr key={idx} className="border-t border-indigo-100">
                                          <td className="px-2 py-1">
                                            <input
                                              type="text"
                                              value={b.name}
                                              onChange={(e) => updateBucket(idx, { name: e.target.value })}
                                              className="w-28 rounded border border-indigo-200 bg-white px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
                                            />
                                          </td>
                                          <td className="px-2 py-1 text-right">
                                            <input
                                              type="number"
                                              min={0}
                                              value={b.low_months}
                                              onChange={(e) => updateBucket(idx, { low_months: Number(e.target.value) || 0 })}
                                              className="w-20 rounded border border-indigo-200 bg-white px-2 py-1 text-right text-xs focus:border-indigo-500 focus:outline-none"
                                            />
                                          </td>
                                          <td className="px-2 py-1 text-right">
                                            <input
                                              type="number"
                                              min={0}
                                              placeholder={isLast ? '∞' : ''}
                                              value={b.high_months ?? ''}
                                              onChange={(e) => updateBucket(idx, { high_months: e.target.value === '' ? null : (Number(e.target.value) || 0) })}
                                              className="w-20 rounded border border-indigo-200 bg-white px-2 py-1 text-right text-xs focus:border-indigo-500 focus:outline-none"
                                            />
                                          </td>
                                          <td className="px-2 py-1 text-center"><TableActionGroup>
                                            <TableActionButton action="remove" label="Remove bucket" type="button" onClick={() => removeBucket(idx)} disabled={currentBuckets.length <= 1} />
                                          </TableActionGroup></td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                                <p className="mt-1 text-[10px] text-indigo-700">
                                  Intervals are half-open <code>[from, to)</code>. The first bucket's <em>From</em> must
                                  equal the honeymoon window; every subsequent bucket's <em>From</em> must equal the
                                  previous bucket's <em>To</em>. The last entry may leave <em>To</em> blank for an
                                  open-ended cohort.
                                </p>
                              </div>
                            </div>
                          );
                        })()}

                        {included && COHORT_SCORED_CODES.has(def.code) && (
                          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                            <strong>Cohort-scored metric — slabs not used.</strong> Points come directly from the
                            cohort-relative engine (AON buckets + honeymoon rule for new joiners); the slab table
                            below is ignored at compute time. Weight still applies as <code>points × weight / 100</code>.
                          </div>
                        )}

                        {/* Direct scoring uses no bands, so the editor is hidden rather
                            than shown empty — an empty ladder reads as "not configured
                            yet" and invites someone to fill it back in. */}
                        {included && !COHORT_SCORED_CODES.has(def.code)
                          && (cfg?.config_params as Record<string, unknown> | null)?.scoring === 'direct' && (
                          <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                            Scored directly from the value — no bands. An average of 68.34 scores 68.34.
                          </div>
                        )}

                        {included && !COHORT_SCORED_CODES.has(def.code)
                          && (cfg?.config_params as Record<string, unknown> | null)?.scoring !== 'direct' && (
                          <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <div className="text-xs font-medium text-gray-600">Slabs</div>
                              <button
                                onClick={() => addSlab(def.metric_def_id)}
                                className="text-xs font-medium text-primary-700 hover:text-primary-900"
                              >
                                + Add slab
                              </button>
                            </div>
                            {/* Slabs are defined on a single 0–100% scale for
                                every metric — the engine normalizes each raw
                                value to a percentage before matching. */}
                            {/* A parameter read from Sales & Targets is sold ÷ target
                                and is not capped: 120% is routine, and a top slab that
                                ends at 100 gives it no points at all. Say that here
                                instead of the 0–100 line, which invites exactly that. */}
                            {def.has_sales_feed ? (
                              <div className="mb-2 text-[11px] text-gray-500">
                                Can go <strong>above 100%</strong>. Leave the top slab’s Upper % empty.
                              </div>
                            ) : (
                              <div className="mb-2 text-[11px] text-gray-500">
                                Slabs are on a <strong>0–100% scale</strong>
                                {NORMALIZED_METRIC_HINT[def.code]
                                  ? ` — ${NORMALIZED_METRIC_HINT[def.code]}.`
                                  : '.'}
                              </div>
                            )}
                            {cfg!.slabs.length === 0 ? (
                              <div className="text-xs text-gray-500">
                                No slabs yet. Add at least one to give this metric any points.
                              </div>
                            ) : (
                              <table className="min-w-full text-xs">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="px-2 py-1 text-left">Lower % (&gt;)</th>
                                    <th className="px-2 py-1 text-left">Upper % (≤)</th>
                                    <th className="px-2 py-1 text-right">%age Achievement</th>
                                    <th className="px-2 py-1 text-left">Label</th>
                                    <th className="text-center px-3 py-2">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cfg!.slabs.map((s, idx) => (
                                    <tr key={idx} className="border-t border-gray-200">
                                      <td className="px-2 py-1">
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={s.lower_bound ?? ''}
                                          placeholder="−∞"
                                          onChange={(e) =>
                                            updateSlab(def.metric_def_id, idx, {
                                              lower_bound: e.target.value === '' ? null : Number(e.target.value),
                                            })
                                          }
                                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                                        />
                                      </td>
                                      <td className="px-2 py-1">
                                        <input
                                          type="number"
                                          step="0.01"
                                          value={s.upper_bound ?? ''}
                                          placeholder="+∞"
                                          onChange={(e) =>
                                            updateSlab(def.metric_def_id, idx, {
                                              upper_bound: e.target.value === '' ? null : Number(e.target.value),
                                            })
                                          }
                                          className="w-24 rounded border border-gray-300 px-2 py-1 text-sm"
                                        />
                                      </td>
                                      <td className="px-2 py-1 text-right">
                                        <input
                                          type="number"
                                          step="0.01"
                                          min={0}
                                          value={s.points}
                                          onChange={(e) =>
                                            updateSlab(def.metric_def_id, idx, {
                                              points: Number(e.target.value) || 0,
                                            })
                                          }
                                          className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm"
                                        />
                                      </td>
                                      <td className="px-2 py-1">
                                        <input
                                          type="text"
                                          value={s.display_label ?? ''}
                                          placeholder="e.g. Excellent"
                                          onChange={(e) =>
                                            updateSlab(def.metric_def_id, idx, {
                                              display_label: e.target.value || null,
                                            })
                                          }
                                          className="w-40 rounded border border-gray-300 px-2 py-1 text-sm"
                                        />
                                      </td>
                                      <td className="px-2 py-1 text-center"><TableActionGroup>
                                        <TableActionButton action="remove" label="Remove slab" onClick={() => removeSlab(def.metric_def_id, idx)} />
                                      </TableActionGroup></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!configLoading && catalog.length === 0 && unavailableMetrics.length === 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-medium">No metrics in the catalog yet for this project.</div>
              <div className="mt-1 text-xs">
                Click <button onClick={openAddMetric} className="font-medium underline">+ Add Metric</button>{' '}
                to define your first target metric. Or, in a task's question editor, toggle{' '}
                <strong>Use in Employee Score</strong> on a numeric question to add it as a
                task-question metric.
              </div>
            </div>
          )}

          {/* System metrics whose underlying data source isn't enabled for
              this project. Shown as guidance — each carries the reason and
              the fix the admin needs to apply before the metric becomes
              selectable above. */}
          {!configLoading && unavailableMetrics.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50/60 shadow-sm">
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-gray-400" aria-hidden />
                  <span className="text-sm font-semibold text-gray-700">Available once captured</span>
                  <span className="text-xs text-gray-500">
                    ({unavailableMetrics.length} metric{unavailableMetrics.length === 1 ? '' : 's'} waiting for data)
                  </span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {unavailableMetrics.map((m) => (
                  <div key={m.code} className="px-4 py-3">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-gray-700">{m.name}</span>
                      {/* machine code intentionally hidden — same reasoning as
                          the Configure rows: end users don't read DB ids. */}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{m.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Legacy 'targets' key (from /master-management/targets redirect) —
          land on the all-metrics TargetsPage so old bookmarks still work. */}
      {tab === 'targets' && !activeMetricTab && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <TargetsPage embedded />
        </div>
      )}

      {/* Trainings + Attendance moved out to /grow/offline-trainings. */}
      {(tab === 'trainings' || tab === 'attendance') && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="font-medium">Offline Trainings has its own module now.</div>
          <div className="mt-1 text-xs">
            Open <a href="/grow/offline-trainings" className="font-medium underline">Grow → Offline Trainings</a>{' '}
            to create trainings, assign employees, and mark attendance. The Employee Score continues to read this
            data via the Training Participation metric.
          </div>
        </div>
      )}

      {/* MANAGE → per-metric sub-tab. Each included metric gets its own tab.
          The tab shows two sections stacked:
            1. Data entry / review surface (only for kinds that have one —
               target_metric → TargetsPage spreadsheet, seed_score → OCR
               review). System and task_question metrics skip this section.
            2. Score table filtered to JUST this metric — employee, store,
               raw value, points, contribution — so the admin can verify the
               slab + weight setup without scrolling the combined View tab. */}
      {activeMetricTab && (
        <div className="space-y-4">
          {/* Section 1 — data entry surface for kinds that have one. */}
          {activeMetricTab.kind === 'target_metric' && (() => {
            // Look up the metric's config row so we can pin the scope
            // (Store vs Employee) the admin chose on Configure — the data
            // tables inside SubMetricsManagePanel use that scope and never
            // re-ask. metric_id falls back to a legacy lookup when the
            // catalog entry is missing the target_metric_id column.
            const activeMetricConfig = config.find(
              (c) => c.metric_def_id === activeMetricTab.metric_def_id,
            );
            const metricId = activeMetricTab.target_metric_id;
            // Project the role-aware scorecard rows down to just this
            // metric so the panel's Per-employee scores tab has the
            // points + weighted contribution numbers ready. The
            // projection is the same one the inline block used to do.
            const metricCode = activeMetricTab.code;
            const projectedScoreRows: MetricScoreRow[] = roleScopedRows
              .map((r): MetricScoreRow | null => {
                const m = r.metrics.find((x) => x.code === metricCode) ?? null;
                if (!m) return null;
                return {
                  employee_code: r.employee_code,
                  employee_name: r.employee_name,
                  store_name: r.store_name,
                  store_code: r.store_code,
                  store_address: r.store_address,
                  value: m.value,
                  points: m.points,
                  contribution: m.contribution,
                  weight_pct: m.weight_pct,
                  // `quadrant` intentionally not projected — the per-metric
                  // Per-employee scores tab dropped that column.
                };
              })
              .filter((r): r is MetricScoreRow => r !== null);
            return (
              <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                {/* "Targets & Achievements" header was removed — it implied
                    every custom metric is a target/achievement pair, which
                    isn't true. The panel below renders its own context-
                    aware header (metric name + formula chip + Manage link)
                    so the wrapper card no longer needs a duplicate title. */}
                {metricId != null && selectedProjectId ? (
                  <SubMetricsManagePanel
                    metricId={metricId}
                    reloadToken={metricDefsNonce}
                    projectId={selectedProjectId}
                    periodMonth={periodMonth}
                    scope={activeMetricConfig?.attribution_scope ?? 'store'}
                    onOpenManage={() => {
                      // The "Manage metric" CTA inside the per-metric
                      // panel opens the EDIT screen for this metric, not
                      // the New screen. Falls back to Add if metricId
                      // isn't available (defensive — shouldn't happen).
                      if (metricId != null) openEditMetric(metricId, periodMonth);
                      else openAddMetric();
                    }}
                    scoreRows={projectedScoreRows}
                    scoreLoading={scoreLoading}
                    scoreError={scoreError}
                    onChangePeriod={(p) => { setPeriodMonth(p); setMonthSource('user'); }}
                    hideMonthPicker
                    configRoleId={configRoleId}
                    roleStoreCodes={roleStoreCodes}
                    exportRef={panelExportRef}
                    onExportableChange={setPanelExportable}
                    onSalesFeedChanged={fetchScore}
                  />
                ) : (
                  // Defensive fallback — the catalog row should always carry
                  // target_metric_id for kind=target_metric, but if it
                  // doesn't (legacy data), fall back to the legacy embed so
                  // admins can still enter data.
                  <TargetsPage
                    embedded
                    lockedMetricId={metricId ?? undefined}
                    lockedScope={activeMetricConfig?.attribution_scope}
                  />
                )}
              </div>
            );
          })()}
          {activeMetricTab.code === 'seed_score' && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="mb-3 text-sm font-semibold text-gray-700">
                Seed Score submissions — review / override
              </h3>
              <AppleScorecardPage embedded restrictTab="seed_scores" />
            </div>
          )}

          {/* Section 2 — per-metric score table. Built from the same
              /api/scorecard payload as the View → Score tab but projected to
              just this one metric so the admin can validate slabs + weights
              row-by-row.

              For target_metric kind the same data is shown INSIDE
              SubMetricsManagePanel as a "Per-employee scores" tab, so the
              standalone block is suppressed there to avoid duplication.
              System + task_question kinds keep the inline block — they
              have no SubMetricsManagePanel of their own. */}
          {activeMetricTab.kind !== 'target_metric' && (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-gray-100 px-4 py-2.5 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-800">Per-employee scores</div>
                <div className="text-xs text-gray-500">
                  How this metric scores against the slabs you've configured for the selected role.
                </div>
                {/* Raw value source — names the engine path the Raw value
                    column reflects so admins can trace where each row's
                    number came from. Re-uses the same METRIC_FORMULAS
                    dict the Configure tab's "How is this calculated?"
                    expander reads, with a per-code lookup falling back
                    to per-kind. */}
                {(() => {
                  const formula = METRIC_FORMULAS[activeMetricTab.code] ?? METRIC_FORMULAS[activeMetricTab.kind];
                  if (!formula) return null;
                  return (
                    <div className="mt-1 text-xs text-gray-500">
                      Raw value = <span className="text-gray-700">{formula.summary}</span>
                    </div>
                  );
                })()}
              </div>
              {/* Month picker moved to the page's top header (shared across all
                  Manage metric sub-tabs); only the search stays inline here. */}
              <div className="flex items-center gap-2">
                <SearchInput value={metricSearch} onChange={setMetricSearch} />
              </div>
            </div>
            {scoreLoading ? (
              <div className="p-6 text-sm text-gray-500"><InlineLoader label="Loading…" className="flex" /></div>
            ) : scoreError ? (
              <div className="p-6 text-sm text-rose-600">{scoreError}</div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-sm text-gray-500">
                No scorecard data for this month yet. Either no role override is configured, or no employees have values on any included metric. Pick a role + define slabs on Configure, then return here.
              </div>
            ) : metricTabScores.rows.length === 0 ? (() => {
              // No employee carries this metric. Distinguish "not configured yet"
              // from "configured, but the engine couldn't compute it". Both leave
              // the metric off every row, but the fixes are completely different —
              // telling an admin to tick-and-save a metric they already ticked and
              // saved sends them in a loop.
              const failure = failedMetrics.find((f) => f.code === activeMetricTab.code);
              if (failure) {
                return (
                  <div className="p-6 text-sm text-amber-700">
                    <div className="font-medium">This metric couldn&apos;t be computed for the selected month.</div>
                    <div className="mt-1 text-amber-600">{failure.error || 'The engine reported no further detail.'}</div>
                    <div className="mt-2 text-gray-500">
                      Your configuration is fine — this is a data-source problem, so it usually clears on its own. Reload in a minute; if it persists, check the API logs for this metric.
                    </div>
                  </div>
                );
              }
              return (
                <div className="p-6 text-sm text-gray-500">
                  No employee in any role&apos;s scorecard includes this metric for the selected month. Open Configure, tick this metric for a role, save, then return here.
                </div>
              );
            })() : (
              // Rows come from the hooks above (metricTabScores →
              // metricTabVisibleRows) — the same ones the Download button writes.
              <PerEmployeeMetricScoreTable
                rows={metricTabVisibleRows}
                weightPct={metricTabScores.weightPct}
                sortKey={metricSortKey}
                sortDir={metricSortDir}
                onSort={handleMetricSort}
                searchTerm={metricSearch}
              />
            )}
          </div>
          )}
        </div>
      )}

      {/* Manage Metrics modal — opened from the Configure tab's "+ Manage
          Metrics" buttons. Closing fires fetchConfig so any newly created
          metric shows up in the Target Metric section right away. */}
      {selectedProjectId && (
        <ManageMetricsModal
          isOpen={metricsModalOpen}
          onClose={() => setMetricsModalOpen(false)}
          projectId={selectedProjectId}
          // The scores are read again too: a parameter just moved to Sales & Targets
          // can leave a finished month waiting, and the bar that says so comes with them.
          onMetricsChanged={() => { fetchConfig(); fetchScore(); setMetricDefsNonce((n) => n + 1); }}
          target={metricsModalTarget}
          effectiveMonth={metricsModalMonth}
        />
      )}
    </div>
  );
}
